import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI, technicalAPI, watchlistAPI, financialsAPI, intradayAPI, portfolioAPI, PortfolioTransaction, isEntityContradicted } from '../services/api';
import { User, PortfolioPosition } from '../types';
import NavBar from '../components/NavBar';
import BackToTop from '../components/BackToTop';
import UpgradeRequired from '../components/UpgradeRequired';
import EntityTrustBlock from '../components/EntityTrustBlock';
import AtrRiskModal, { AtrRisk } from '../components/AtrRiskLevels';
import { formatUsd } from '../utils/currency';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Chart } from 'react-chartjs-2';
import { AreaChart, Area, Line as RLine, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend as RLegend, ResponsiveContainer } from 'recharts';
import { formatDateOnly } from '../utils/dates';
import { setActiveTicker } from '../utils/activeTicker';


// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface TechnicalAnalysisData {
  ticker: string;
  company_name: string;
  security_type?: string;  // CS, WARRANT, ETF, etc. from Polygon
  current_price: number;
  analysis_date: string;
  indicators: {
    rsi: {
      value: number;
      signal: string;
      description: string;
    };
    macd: {
      macd_line: number;
      signal_line: number;
      histogram: number;
      trend: string;
      description: string;
    };
    moving_averages: {
      sma_20: number;
      sma_50: number;
      sma_200: number;
      above_sma_20: boolean;
      above_sma_50: boolean;
      description: string;
    };
    bollinger_bands: {
      upper_band: number;
      middle_band: number;
      lower_band: number;
      position: string;
      description: string;
    };
    squeeze?: {
      state: 'on' | 'fired' | 'none';
      bars_in_squeeze: number;
      bars_since_fire: number | null;
      min_bars: number;
    } | null;
    // ── Advanced Indicators ────────────────────────────
    vwap?: { value: number; signal: string; description: string } | null;
    obv?: { value: number; signal: string; description: string } | null;
    ad_line?: { value: number; signal: string; description: string } | null;
    stochastic?: { k: number; d: number; signal: string; description: string } | null;
    adx?: { adx: number; plus_di: number; minus_di: number; strength: string; direction: string; description: string } | null;
    cci?: { value: number; signal: string; description: string } | null;
    roc?: { value: number; signal: string; description: string } | null;
    atr?: { value: number; percent: number; volatility: string; description: string } | null;
    atr_risk?: AtrRisk | null;
    keltner?: { upper: number; middle: number; lower: number; position: string; description: string } | null;
    std_dev?: { value: number; percent: number; description: string } | null;
    parabolic_sar?: { value: number; trend: string; description: string } | null;
    ichimoku?: { tenkan: number; kijun: number; senkou_a: number; senkou_b: number; signal: string; description: string } | null;
    donchian?: { upper: number; lower: number; middle: number; description: string } | null;
  };
  signals: Array<{
    type: string;
    indicator: string;
    message: string;
    scored?: boolean;
  }>;
  chart_data: Array<{
    date: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
    sma_20: number | null;
    sma_50: number | null;
    sma_200: number | null;
    bb_upper: number | null;
    bb_middle: number | null;
    bb_lower: number | null;
    rsi: number | null;
    macd_line: number | null;
    macd_signal: number | null;
    macd_histogram: number | null;
    // Advanced
    vwap?: number | null;
    obv?: number | null;
    ad_line?: number | null;
    stoch_k?: number | null;
    stoch_d?: number | null;
    adx?: number | null;
    plus_di?: number | null;
    minus_di?: number | null;
    cci?: number | null;
    roc?: number | null;
    atr?: number | null;
    chandelier_long?: number | null;
    chandelier_short?: number | null;
    keltner_upper?: number | null;
    keltner_middle?: number | null;
    keltner_lower?: number | null;
    std_dev?: number | null;
    sar?: number | null;
    sar_trend?: number | null;
    ichimoku_tenkan?: number | null;
    ichimoku_kijun?: number | null;
    ichimoku_senkou_a?: number | null;
    ichimoku_senkou_b?: number | null;
    donchian_upper?: number | null;
    donchian_lower?: number | null;
    donchian_middle?: number | null;
  }>;
  summary: {
    outlook: string;
    strength: number;
    message: string;
    price_range?: {
      support: number;
      resistance: number;
      support_levels_count: number;
      resistance_levels_count: number;
    } | null;
  };
}

// ─── True Intraday VWAP Modal ────────────────────────────────────────────────

interface TrueVwapBar {
  timestamp: string;
  high: number | null;
  low: number | null;
  close: number | null;
  volume: number | null;
}

const TrueVwapModal: React.FC<{ ticker: string; onClose: () => void }> = ({ ticker, onClose }) => {
  const [barsData, setBarsData] = useState<{ bars: TrueVwapBar[]; data_date: string; is_today: boolean; count: number; multiplier: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setFetchError(null);
    intradayAPI.getBarsWithMA(ticker)
      .then(res => setBarsData(res.data))
      .catch(err => setFetchError(err.response?.data?.detail || 'Failed to load intraday data'))
      .finally(() => setLoading(false));
  }, [ticker]);

  const chartData = useMemo(() => {
    if (!barsData?.bars?.length) return [];
    let cumTpVol = 0;
    let cumVol = 0;
    return barsData.bars.map(bar => {
      const h = bar.high ?? 0;
      const l = bar.low ?? 0;
      const c = bar.close ?? 0;
      const v = bar.volume ?? 0;
      const tp = (h + l + c) / 3;
      cumTpVol += tp * v;
      cumVol += v;
      const vwap = cumVol > 0 ? cumTpVol / cumVol : tp;
      return {
        time: new Date(bar.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        price: c || null,
        vwap: Math.round(vwap * 10000) / 10000,
      };
    });
  }, [barsData]);

  const lastPoint = chartData[chartData.length - 1];
  const currentVwap = lastPoint?.vwap ?? null;
  const currentPrice = lastPoint?.price ?? null;
  const isAbove = currentPrice != null && currentVwap != null && currentPrice > currentVwap;

  const fmtDyn = (v: number | null) =>
    v == null ? 'N/A' : `$${v < 10 ? v.toFixed(2) : v < 100 ? v.toFixed(1) : v.toFixed(0)}`;
  const fmt2 = (v: number | null) => v == null ? 'N/A' : `$${v.toFixed(2)}`;

  const dateLabel = barsData?.data_date
    ? formatDateOnly(barsData.data_date)
    : '';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl flex flex-col overflow-hidden"
        style={{ maxHeight: '75vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              True Intraday VWAP — {ticker}
            </h2>
            {dateLabel && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {barsData?.is_today ? 'Today' : dateLabel}
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600" />
            </div>
          )}
          {fetchError && !loading && (
            <div className="text-center text-red-400 py-12">{fetchError}</div>
          )}
          {!loading && !fetchError && chartData.length > 0 && (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Session VWAP resets at market open and accumulates throughout the day. Price above VWAP = institutional buy pressure; below = sell pressure.
                Calculated from <strong>{barsData?.count ?? chartData.length}</strong> bars ({barsData?.multiplier}-min intervals).
              </p>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="tvwapPriceFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={isAbove ? 'rgb(16,185,129)' : 'rgb(239,68,68)'} stopOpacity={0.5} />
                      <stop offset="95%" stopColor={isAbove ? 'rgb(16,185,129)' : 'rgb(239,68,68)'} stopOpacity={0.03} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" style={{ fontSize: '11px' }} interval="preserveStartEnd" />
                  <YAxis
                    orientation="right"
                    stroke="#9ca3af"
                    domain={['auto', 'auto']}
                    style={{ fontSize: '11px' }}
                    tickFormatter={fmtDyn}
                    width={55}
                  />
                  <RTooltip
                    formatter={(value: any) => [fmt2(value as number)]}
                    contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#f9fafb' }}
                    labelStyle={{ color: '#9ca3af', marginBottom: 4 }}
                  />
                  <RLegend wrapperStyle={{ fontSize: '12px' }} />
                  <Area
                    type="monotone"
                    dataKey="price"
                    name="Price"
                    stroke={isAbove ? 'rgb(16,185,129)' : 'rgb(239,68,68)'}
                    strokeWidth={2}
                    fill="url(#tvwapPriceFill)"
                    dot={false}
                    isAnimationActive={false}
                  />
                  <RLine
                    type="monotone"
                    dataKey="vwap"
                    name="VWAP"
                    stroke="rgb(245, 158, 11)"
                    strokeWidth={2}
                    strokeDasharray="6 3"
                    dot={false}
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </>
          )}
        </div>

        {/* Footer stats */}
        {!loading && !fetchError && currentVwap != null && (
          <div className="flex items-center gap-6 px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 shrink-0">
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Session VWAP</div>
              <div className="text-base font-bold text-amber-500">{fmt2(currentVwap)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Last Price</div>
              <div className={`text-base font-bold ${isAbove ? 'text-green-500' : 'text-red-400'}`}>{fmt2(currentPrice)}</div>
            </div>
            <span className={`px-2 py-1 text-xs font-bold rounded ${isAbove ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300'}`}>
              {isAbove ? 'ABOVE VWAP' : 'BELOW VWAP'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

// Maps a signal's indicator name to the chart section that renders it, plus the
// collapsible category that section lives under (null = always visible).
// Single source of truth for both the Trading Signals scroll targets and the
// signal badge shown in each chart header.
type ChartCategory = 'volume' | 'momentum' | 'volatility' | 'trend' | null;

const chartTargetForIndicator = (indicator: string): { chartId: string; category: ChartCategory } | null => {
  const name = indicator.toLowerCase();
  // Basic charts (always visible)
  if (name.includes('bollinger')) return { chartId: 'chart-bollinger', category: null };
  if (name.includes('moving average') || name.includes('golden cross') || name.includes('death cross') || name === 'sma' || name === 'ema') return { chartId: 'chart-moving-averages', category: null };
  if (name.includes('rsi') || name === 'relative strength index') return { chartId: 'chart-rsi', category: null };
  if (name.includes('macd')) return { chartId: 'chart-macd', category: null };
  // Volume indicators
  if (name.includes('vwap')) return { chartId: 'chart-vwap', category: 'volume' };
  if (name.includes('a/d') || name.includes('accumulation')) return { chartId: 'chart-ad', category: 'volume' };
  if (name.includes('obv') || name.includes('on-balance')) return { chartId: 'chart-obv', category: 'volume' };
  // Momentum indicators
  if (name.includes('stochastic') || name.includes('stoch')) return { chartId: 'chart-stochastic', category: 'momentum' };
  if (name.includes('adx') || name.includes('directional')) return { chartId: 'chart-adx', category: 'momentum' };
  if (name.includes('cci')) return { chartId: 'chart-cci', category: 'momentum' };
  if (name.includes('roc') || name.includes('rate of change')) return { chartId: 'chart-roc', category: 'momentum' };
  // Volatility indicators
  if (name.includes('atr') || name.includes('average true range')) return { chartId: 'chart-atr', category: 'volatility' };
  if (name.includes('keltner')) return { chartId: 'chart-keltner', category: 'volatility' };
  if (name.includes('std') || name.includes('standard dev')) return { chartId: 'chart-volatility-summary', category: 'volatility' };
  // Trend indicators
  if (name.includes('parabolic') || name.includes('sar')) return { chartId: 'chart-parabolic-sar', category: 'trend' };
  if (name.includes('ichimoku')) return { chartId: 'chart-ichimoku', category: 'trend' };
  if (name.includes('donchian')) return { chartId: 'chart-donchian', category: 'trend' };
  return null;
};

const signalPillClass = (type: string): string =>
  type === 'bullish' ? 'bg-green-500' : type === 'bearish' ? 'bg-red-500' : 'bg-amber-500';

const TechnicalAnalysis: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlTicker = searchParams.get('ticker') || '';
  const [user, setUser] = useState<User | null>(null);
  const [ticker, setTicker] = useState(urlTicker);
  const [loading, setLoading] = useState(false);
  const [analysisData, setAnalysisData] = useState<TechnicalAnalysisData | null>(null);
  // Entry anchors for the ATR calculator. Best-effort: a failure here leaves
  // the calculator seeded from the last close and is never surfaced as an error.
  const [atrPosition, setAtrPosition] = useState<PortfolioPosition | null>(null);
  const [atrLots, setAtrLots] = useState<PortfolioTransaction[]>([]);
  const [atrLotsTruncated, setAtrLotsTruncated] = useState(false);
  const [atrLotsBeforeAdjust, setAtrLotsBeforeAdjust] = useState(false);
  const [showAtrCalc, setShowAtrCalc] = useState(false);
  const [error, setError] = useState('');
  const [isWarrant, setIsWarrant] = useState(false);
  const [relatedCommonStock, setRelatedCommonStock] = useState<string | null>(null);
  
  // AI stock analysis
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [aiGeneratedAt, setAiGeneratedAt] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);

  // Advanced indicator panel toggles
  const [showVolume, setShowVolume] = useState(false);
  const [showMomentum, setShowMomentum] = useState(false);
  const [showVolatility, setShowVolatility] = useState(false);
  const [showTrend, setShowTrend] = useState(false);

  // Jump to the chart section a Trading Signals card refers to, expanding its
  // collapsible category first when the section is hidden.
  const scrollToChart = (indicator: string) => {
    const target = chartTargetForIndicator(indicator);
    if (!target) return;

    const expanders: Record<string, () => void> = {
      volume: () => setShowVolume(true),
      momentum: () => setShowMomentum(true),
      volatility: () => setShowVolatility(true),
      trend: () => setShowTrend(true),
    };
    const expandCategory = target.category ? expanders[target.category] : null;

    // Expand the category if needed, then scroll after React re-renders
    if (expandCategory) {
      expandCategory();
      setTimeout(() => {
        document.getElementById(target.chartId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } else {
      document.getElementById(target.chartId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Right-justified restatement of the Trading Signals card for a given chart
  // section, so the read is available without scrolling back up. Renders
  // nothing when no signal maps to this chart (e.g. ATR).
  const renderChartSignal = (chartId: string) => {
    const signal = analysisData?.signals?.find(s => chartTargetForIndicator(s.indicator)?.chartId === chartId);
    if (!signal) return null;
    return (
      <div className="sm:flex-shrink-0 sm:max-w-[400px] sm:text-right">
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold text-white ${signalPillClass(signal.type)}`}>
          {signal.type.charAt(0).toUpperCase() + signal.type.slice(1)}
        </span>
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 font-normal">{signal.message}</p>
      </div>
    );
  };

  const [watchlistMsg, setWatchlistMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [addingToWatchlist, setAddingToWatchlist] = useState(false);
  // Quota is enforced and reported by the server; the browser never counts.
  // Populated from the 403 detail, same as DCFValuation.tsx.
  const [limitData, setLimitData] = useState<{ currentUsage: number; maxUsage: number; period: string } | null>(null);

  // Trend line drawing state (Moving Averages chart)
  const maChartRef = useRef<any>(null);
  const [trendDrawingMode, setTrendDrawingMode] = useState(false);
  const [trendAwaitingSecond, setTrendAwaitingSecond] = useState(false);
  const pendingTrendPointRef = useRef<{ xFraction: number; yValue: number } | null>(null);
  const drawnTrendLinesRef = useRef<Array<{ x1: number; y1: number; x2: number; y2: number }>>([]);
  const [trendLineCount, setTrendLineCount] = useState(0); // triggers chart re-draw

  // Shared vertical reference line across every TA chart.
  // `verticalLineTimestamp` is the single source of truth (epoch ms of the snapped
  // data point); `verticalLineIndexRef` mirrors it as a chart_data index so the
  // Chart.js plugin can read the current position without a stale closure.
  const [verticalLineTimestamp, setVerticalLineTimestamp] = useState<number | null>(null);
  const verticalLineIndexRef = useRef<number | null>(null);

  // Financials panel state
  const [showFinancials, setShowFinancials] = useState(false);
  const [showTrueVwap, setShowTrueVwap] = useState(false);
  const [financialsData, setFinancialsData] = useState<any>(null);
  const [financialsLoading, setFinancialsLoading] = useState(false);
  const [financialsError, setFinancialsError] = useState<string | null>(null);

  // AI read of the financial statements (separate from the technicals AI summary
  // above; both share the same ai_analysis usage pool on the backend)
  const [finAiSummary, setFinAiSummary] = useState<string | null>(null);
  const [finAiGeneratedAt, setFinAiGeneratedAt] = useState<string | null>(null);
  const [finAiLoading, setFinAiLoading] = useState(false);
  const [finAiError, setFinAiError] = useState<string | null>(null);
  const [finAiSuppressed, setFinAiSuppressed] = useState(false);

  // Warrant detection is now API-driven using Polygon's `type` field (CS, WARRANT, ETF, etc.)
  // These helpers are only used as a pre-fetch hint for explicit separator patterns
  const detectWarrantHint = (tickerSymbol: string): boolean => {
    const upper = tickerSymbol.toUpperCase();
    return (
      upper.includes('.W') ||
      upper.includes('+W') ||
      upper.endsWith('/WS') ||
      upper.endsWith('/WT')
    );
  };

  const getRelatedCommonStock = (warrantTicker: string): string | null => {
    const upper = warrantTicker.toUpperCase();
    const patterns = ['.WS', '.WT', '.W', '+WS', '+WT', '+W', '/WS', '/WT', '/W'];
    for (const pat of patterns) {
      if (upper.endsWith(pat)) {
        return upper.slice(0, -pat.length);
      }
    }
    return null;
  };

  React.useEffect(() => {
    loadUser();
    // Auto-analyze if ticker is provided via URL
    if (urlTicker) {
      setTicker(urlTicker);

      // Pre-fetch hint only — real warrant detection happens after API response
      const isWarrantHint = detectWarrantHint(urlTicker);
      setIsWarrant(isWarrantHint);
      if (isWarrantHint) {
        setRelatedCommonStock(getRelatedCommonStock(urlTicker));
      }
      performAnalysis(urlTicker);
    }
  }, [urlTicker]);

  const loadUser = async () => {
    try {
      const response = await authAPI.getCurrentUser();
      setUser(response.data);
    } catch (error) {
      console.error('Failed to load user:', error);
      if ((error as any).response?.status === 401) {
        localStorage.removeItem('access_token');
        navigate('/login');
      }
    }
  };

  // Entry anchors for the ATR calculator, read from the portfolio ledger.
  // Fire-and-forget: never blocks the page and never raises. The ledger is
  // append-only, so the current holding is whatever sits at or after the most
  // recent ADJUST checkpoint — buys before it are real history but no longer
  // describe this position, and offering one as "your entry" would be wrong.
  const loadEntryAnchors = async (symbol: string) => {
    const [posRes, txRes] = await Promise.allSettled([
      portfolioAPI.getAll(),
      portfolioAPI.getTransactions({ ticker: symbol, limit: 100 }),
    ]);

    let held: PortfolioPosition | null = null;
    if (posRes.status === 'fulfilled') {
      const rows: PortfolioPosition[] = posRes.value.data?.positions ?? posRes.value.data ?? [];
      held = Array.isArray(rows)
        ? rows.find(r => r.ticker?.toUpperCase() === symbol) ?? null
        : null;
      setAtrPosition(held);
    }

    if (txRes.status !== 'fulfilled') return;
    const { transactions = [], total = 0 } = txRes.value.data ?? {};
    // Composite sort key: trade date first, created_at only as a tiebreak.
    // Both are lexicographically sortable, so string compare is the right order.
    const keyOf = (t: PortfolioTransaction) => t.transaction_date + '#' + t.created_at;
    const checkpoint = transactions
      .filter(t => t.transaction_type === 'ADJUST')
      .map(keyOf)
      .sort()
      .pop();

    const buys = transactions.filter(t => t.transaction_type === 'BUY' && !t.is_voided);
    const current = checkpoint ? buys.filter(t => keyOf(t) >= checkpoint) : buys;
    current.sort((a, b) => keyOf(b).localeCompare(keyOf(a)));

    setAtrLots(current);
    setAtrLotsTruncated(total > transactions.length);
    setAtrLotsBeforeAdjust(current.length === 0 && buys.length > 0);
  };

  const performAnalysis = async (symbol: string) => {
    if (!symbol.trim()) {
      setError('Please enter a ticker symbol');
      return;
    }

    setLoading(true);
    setError('');
    setAnalysisData(null);
    setFinancialsData(null);
    setShowFinancials(false);
    setFinancialsError(null);
    setAiSummary(null);
    setAiGeneratedAt(null);
    resetFinancialsAi();
    setAtrPosition(null);
    setAtrLots([]);
    setAtrLotsTruncated(false);
    setAtrLotsBeforeAdjust(false);
    setShowAtrCalc(false);
    setLimitData(null);

    try {
      const response = await technicalAPI.analyze(symbol.toUpperCase());
      setAnalysisData(response.data);
      setActiveTicker(symbol);
      loadEntryAnchors(symbol.toUpperCase());
      
      // API-driven warrant detection — overrides any pre-fetch hint
      const apiType = response.data.security_type || '';
      if (apiType === 'WARRANT') {
        setIsWarrant(true);
        const related = getRelatedCommonStock(symbol);
        if (related) {
          setRelatedCommonStock(related);
        } else {
          const stripped = symbol.toUpperCase().replace(/W+$/, '');
          setRelatedCommonStock(stripped.length >= 2 && stripped !== symbol.toUpperCase() ? stripped : null);
        }
      } else if (apiType === 'CS' || apiType === 'ADRC' || apiType === 'PFD') {
        setIsWarrant(false);
        setRelatedCommonStock(null);
      }
    } catch (err: any) {
      console.error('Analysis error:', err);
      if (err.response?.status === 403 && err.response?.data?.detail?.current_usage !== undefined) {
        setLimitData({
          currentUsage: err.response.data.detail.current_usage,
          maxUsage: err.response.data.detail.max_usage,
          period: err.response.data.detail.period,
        });
      } else {
        setError(err.response?.data?.detail || 'Failed to analyze stock. Please check the ticker symbol.');
      }
    } finally {
      setLoading(false);
    }
  };


  const handleAnalyze = async (e: React.FormEvent) => {
      e.preventDefault();
      await performAnalysis(ticker);
  };

  const handleAiAnalysis = async () => {
    if (!analysisData) return;
    setAiLoading(true);
    try {
      const response = await technicalAPI.aiAnalysis(analysisData.ticker);
      setAiSummary(response.data.summary);
      setAiGeneratedAt(response.data.generated_at);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 403) {
        const msg = typeof detail === 'object' ? detail.message : detail;
        setAiSummary(msg || 'AI Analysis is not available on your current plan.');
      } else {
        setAiSummary('Unable to generate analysis at this time. Please try again.');
      }
      setAiGeneratedAt(null);
    } finally {
      setAiLoading(false);
    }
  };

  const resetFinancialsAi = () => {
    setFinAiSummary(null);
    setFinAiGeneratedAt(null);
    setFinAiError(null);
    setFinAiSuppressed(false);
    setFinAiLoading(false);
  };

  // Explicit click only — never on page load or on opening the panel. Most users
  // open Financial Summary just to read the ratios, and auto-firing would spend an
  // ai_analysis use every time (a Casual user only gets 5 a week).
  const handleFinancialsAi = async (refresh = false) => {
    const t = analysisData?.ticker || ticker.trim().toUpperCase();
    if (!t) return;

    setFinAiLoading(true);
    setFinAiError(null);
    try {
      const response = await financialsAPI.aiFinancials(t, refresh);
      if (response.data?.suppressed || !response.data?.summary) {
        // Suppressed is a normal outcome (fund, warrant, stale filings, nothing
        // notable). Render nothing rather than explaining why the AI declined.
        setFinAiSuppressed(true);
        setFinAiSummary(null);
        setFinAiGeneratedAt(null);
      } else {
        setFinAiSuppressed(false);
        setFinAiSummary(response.data.summary);
        setFinAiGeneratedAt(response.data.generated_at);
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 403) {
        const msg = typeof detail === 'object' ? detail.message : detail;
        setFinAiError(msg || 'AI analysis is not available on your current plan.');
      } else {
        setFinAiError(
          typeof detail === 'string'
            ? detail
            : 'Unable to generate a financials read at this time. Please try again.'
        );
      }
      setFinAiSummary(null);
      setFinAiGeneratedAt(null);
    } finally {
      setFinAiLoading(false);
    }
  };

  const handleTickerChange = (value: string) => {
    setTicker(value);
    
    // Pre-fetch hint only — real warrant detection happens after API response
    const isWarrantHint = detectWarrantHint(value);
    setIsWarrant(isWarrantHint);
    if (isWarrantHint) {
      setRelatedCommonStock(getRelatedCommonStock(value));
    } else {
      setRelatedCommonStock(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    navigate('/');
  };

  const handleAddToWatchlist = async () => {
    const t = ticker.trim().toUpperCase() || analysisData?.ticker;
    if (!t) return;
    setAddingToWatchlist(true);
    setWatchlistMsg(null);
    try {
      const payload: any = { ticker: t };
      if (analysisData?.current_price) {
        payload.target_price = analysisData.current_price;
      }
      if (analysisData?.company_name) {
        payload.notes = analysisData.company_name;
      }
      await watchlistAPI.add(payload);
      setWatchlistMsg({ type: 'success', text: `${t} added to watchlist!` });
      setTimeout(() => setWatchlistMsg(null), 3000);
    } catch (err: any) {
      const msg = err.response?.data?.detail || 'Failed to add to watchlist';
      setWatchlistMsg({ type: 'error', text: typeof msg === 'string' ? msg : 'Failed to add to watchlist' });
      setTimeout(() => setWatchlistMsg(null), 4000);
    } finally {
      setAddingToWatchlist(false);
    }
  };

  // ── Financials helpers ───────────────────────────────────────────
  // Every money figure in the payload is dollars; the backend converts foreign
  // filers (see `usd_conversion`).
  const fmtB = (val: number | null | undefined): string => formatUsd(val);

  const fmtPct = (val: number | null | undefined): string => {
    if (val == null) return '—';
    return `${val.toFixed(1)}%`;
  };

  const fmtNum = (val: number | null | undefined, decimals = 2): string => {
    if (val == null) return '—';
    return val.toFixed(decimals);
  };

  // Cash + short-term investments. Mirrors `liquid_assets` in the backend's
  // financial_fact_block so this card and the AI read above it cannot disagree:
  // absent short-term investments count as zero, but absent cash is unknown, not
  // zero. Showing cash alone understated PFE by 10.7B (976M vs 11.70B).
  const liquidAssets = (bs: any): number | null => {
    const cash = bs?.cash_and_equivalents;
    if (cash == null) return null;
    return cash + (bs?.short_term_investments ?? 0);
  };

  const handleFetchFinancials = async () => {
    const t = analysisData?.ticker || ticker.trim().toUpperCase();
    if (!t) return;

    // Toggle off if already showing
    if (showFinancials && financialsData) {
      setShowFinancials(false);
      return;
    }

    setFinancialsLoading(true);
    setFinancialsError(null);
    setShowFinancials(true);

    try {
      const response = await financialsAPI.get(t);
      setFinancialsData(response.data);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403) {
        setFinancialsError('Financial summaries require a paid subscription.');
      } else if (err.response?.status === 404) {
        setFinancialsError(typeof detail === 'string' ? detail : `No financial data available for ${t}.`);
      } else {
        setFinancialsError(typeof detail === 'string' ? detail : 'Failed to load financial data.');
      }
    } finally {
      setFinancialsLoading(false);
    }
  };

  // Reset financials when ticker changes
  React.useEffect(() => {
    setFinancialsData(null);
    setShowFinancials(false);
    setFinancialsError(null);
    resetFinancialsAi();
  }, [ticker]);

  // Chart configurations
  const getBBChartData = () => {
    if (!analysisData) return null;

    const dates = analysisData.chart_data.map(d => d.date);
    const prices = analysisData.chart_data.map(d => d.close);
    const volumes = analysisData.chart_data.map(d => d.volume);
    const bbUpper = analysisData.chart_data.map(d => d.bb_upper);
    const bbLower = analysisData.chart_data.map(d => d.bb_lower);

    return {
        labels: dates,
        datasets: [
        {
            label: 'Price',
            data: prices,
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.1,
            pointRadius: 0,
            yAxisID: 'y',
        },
        {
            label: 'Upper BB',
            data: bbUpper,
            borderColor: 'rgb(239, 68, 68)',
            borderWidth: 1.5,
            fill: false,
            tension: 0.1,
            pointRadius: 0,
            borderDash: [3, 3],
            yAxisID: 'y',
        },
        {
            label: 'Lower BB',
            data: bbLower,
            borderColor: 'rgb(34, 197, 94)',
            borderWidth: 1.5,
            fill: false,
            tension: 0.1,
            pointRadius: 0,
            borderDash: [3, 3],
            yAxisID: 'y',
        },
        {
            label: 'Volume',
            data: volumes,
            backgroundColor: 'rgba(156, 163, 175, 0.3)',
            borderColor: 'rgba(156, 163, 175, 0.5)',
            borderWidth: 1,
            type: 'bar' as const,
            yAxisID: 'y1',
        },
        ],
    };
  };

  const getMAChartData = () => {
    if (!analysisData) return null;

    const dates = analysisData.chart_data.map(d => d.date);
    const prices = analysisData.chart_data.map(d => d.close);
    const sma20 = analysisData.chart_data.map(d => d.sma_20);
    const sma50 = analysisData.chart_data.map(d => d.sma_50);

    return {
        labels: dates,
        datasets: [
        {
            label: 'Price',
            data: prices,
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.1,
            pointRadius: 0,
        },
        {
            label: '20-day SMA',
            data: sma20,
            borderColor: 'rgb(234, 179, 8)',
            borderWidth: 2,
            fill: false,
            tension: 0.1,
            pointRadius: 0,
        },
        {
            label: '50-day SMA',
            data: sma50,
            borderColor: 'rgb(168, 85, 247)',
            borderWidth: 2,
            fill: false,
            tension: 0.1,
            pointRadius: 0,
        },
        ],
    };
  };

  const getRSIChartData = () => {
    if (!analysisData) return null;

    const dates = analysisData.chart_data.map(d => d.date);
    const rsiValues = analysisData.chart_data.map(d => d.rsi);

    return {
      labels: dates,
      datasets: [
        {
          label: 'RSI',
          data: rsiValues,
          borderColor: 'rgb(99, 102, 241)',
          backgroundColor: 'rgba(99, 102, 241, 0.1)',
          borderWidth: 2,
          fill: true,
          tension: 0.1,
          pointRadius: 0,
        },
        {
          label: 'Oversold (30)',
          data: Array(dates.length).fill(30),
          borderColor: 'rgb(34, 197, 94)',
          borderWidth: 1,
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false,
        },
        {
          label: 'Overbought (70)',
          data: Array(dates.length).fill(70),
          borderColor: 'rgb(239, 68, 68)',
          borderWidth: 1,
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false,
        },
      ],
    };
  };

  const getMACDChartData = () => {
    if (!analysisData) return null;

    const dates = analysisData.chart_data.map(d => d.date);
    const macdLine = analysisData.chart_data.map(d => d.macd_line);
    const signalLine = analysisData.chart_data.map(d => d.macd_signal);
    const histogram = analysisData.chart_data.map(d => d.macd_histogram);

    return {
      labels: dates,
      datasets: [
        {
          label: 'MACD Line',
          data: macdLine,
          borderColor: 'rgb(59, 130, 246)',
          borderWidth: 2,
          fill: false,
          tension: 0.1,
          pointRadius: 0,
          type: 'line' as const,
        },
        {
          label: 'Signal Line',
          data: signalLine,
          borderColor: 'rgb(239, 68, 68)',
          borderWidth: 2,
          fill: false,
          tension: 0.1,
          pointRadius: 0,
          type: 'line' as const,
        },
        {
          label: 'Histogram',
          data: histogram,
          backgroundColor: histogram.map((val: number | null) => 
            val && val >= 0 ? 'rgba(34, 197, 94, 0.5)' : 'rgba(239, 68, 68, 0.5)'
          ),
          borderColor: 'transparent',
          borderWidth: 0,
          type: 'bar' as const,
        },
      ],
    };
  };

  // ─── Shared vertical reference line ────────────────────────────────────────
  // Every TA chart registers itself through `verticalLinePlugin` so a click on
  // any one of them can redraw the line on all of them.
  const taChartsRef = useRef<Set<any>>(new Set());

  const redrawTACharts = () => {
    taChartsRef.current.forEach(chart => {
      try {
        chart.update('none');
      } catch {
        /* chart already torn down */
      }
    });
  };

  // Read-only: is any drawing tool currently armed? Vertical-line clicks defer
  // to the tool's own click handler. New tools should be OR'd in here.
  const isDrawingToolActive = trendDrawingMode;

  const setVerticalLine = (index: number | null) => {
    verticalLineIndexRef.current = index;
    setVerticalLineTimestamp(
      index === null || !analysisData?.chart_data[index]
        ? null
        : new Date(analysisData.chart_data[index].date).getTime()
    );
    redrawTACharts();
  };

  // A stale index would point at the wrong bar once new data loads.
  React.useEffect(() => {
    verticalLineIndexRef.current = null;
    setVerticalLineTimestamp(null);
  }, [analysisData]);

  const verticalLinePlugin = useMemo(() => ({
    id: 'sharedVerticalLine',
    afterInit: (chart: any) => {
      taChartsRef.current.add(chart);
    },
    afterDestroy: (chart: any) => {
      taChartsRef.current.delete(chart);
    },
    afterDraw: (chart: any) => {
      const index = verticalLineIndexRef.current;
      if (index === null) return;
      const area = chart.chartArea;
      const xScale = chart.scales?.x;
      if (!area || !xScale) return;
      const px = xScale.getPixelForValue(index);
      if (!isFinite(px) || px < area.left || px > area.right) return;

      const ctx = chart.ctx;
      const isDark = document.documentElement.classList.contains('dark');
      ctx.save();
      ctx.beginPath();
      ctx.setLineDash([4, 4]);
      ctx.lineWidth = 1;
      ctx.strokeStyle = isDark ? '#ffffff' : '#000000';
      ctx.moveTo(px, area.top);
      ctx.lineTo(px, area.bottom);
      ctx.stroke();
      ctx.restore();
    },
  }), []);

  // Chart.js-level click: drops the shared line on the nearest data point.
  const handleVerticalLineClick = (event: any, _elements: any[], chart: any) => {
    if (isDrawingToolActive) return;
    if (!analysisData?.chart_data.length) return;
    const area = chart?.chartArea;
    const xScale = chart?.scales?.x;
    if (!area || !xScale) return;
    const px = event?.x;
    if (typeof px !== 'number' || px < area.left || px > area.right) return;
    // CategoryScale.getValueForPixel already rounds to the nearest index
    const raw = xScale.getValueForPixel(px);
    if (raw == null || !isFinite(raw)) return;
    setVerticalLine(Math.max(0, Math.min(analysisData.chart_data.length - 1, Math.round(raw))));
  };

  // Rendered inside each chart container (which must be `relative`).
  const clearLineButton = verticalLineTimestamp !== null ? (
    <button
      onClick={() => setVerticalLine(null)}
      className="absolute top-0 left-0 z-10 px-2 py-0.5 text-xs font-medium rounded border bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors"
    >
      Clear line
    </button>
  ) : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: handleVerticalLineClick,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: '#9CA3AF',
        },
      },
      tooltip: {
        mode: 'index' as const,
        intersect: true,
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#9CA3AF',
        },
        grid: {
          color: 'rgba(156, 163, 175, 0.1)',
        },
      },
      y: {
        position: 'right' as const,
        ticks: {
          color: '#9CA3AF',
        },
        grid: {
          color: 'rgba(156, 163, 175, 0.1)',
        },
      },
    },
  };

  const priceChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: handleVerticalLineClick,
    interaction: {
        mode: 'index' as const,
        intersect: true,
    },
    plugins: {
        legend: {
        position: 'top' as const,
        labels: {
            color: '#9CA3AF',
            usePointStyle: true,
        },
        },
        tooltip: {
        mode: 'index' as const,
        intersect: true,
        },
    },
    scales: {
        x: {
        ticks: {
            color: '#9CA3AF',
        },
        grid: {
            color: 'rgba(156, 163, 175, 0.1)',
        },
        },
        y: {
        type: 'linear' as const,
        position: 'right' as const,
        ticks: {
            color: '#9CA3AF',
            callback: (v: number | string) => `$${Number(v).toFixed(2)}`,
        },
        grid: {
            color: 'rgba(156, 163, 175, 0.1)',
        },
        },
        y1: {
        type: 'linear' as const,
        position: 'left' as const,
        ticks: {
            color: '#9CA3AF',
        },
        grid: {
            drawOnChartArea: false,
        },
        },
    },
  };

  // Custom Chart.js plugin that draws user-defined trend lines on the MA chart
  const trendLinePlugin = {
    id: 'trendLineDrawer',
    afterDraw: (chart: any) => {
      const ctx = chart.ctx;
      const area = chart.chartArea;
      if (!area) return;
      const isDark = document.documentElement.classList.contains('dark');
      const lineColor = isDark ? '#ffffff' : '#000000';
      const areaWidth = area.right - area.left;

      const toPixel = (pt: { xFraction: number; yValue: number }) => ({
        px: area.left + pt.xFraction * areaWidth,
        py: chart.scales.y.getPixelForValue(pt.yValue),
      });

      // Draw completed lines
      drawnTrendLinesRef.current.forEach(line => {
        const p1 = toPixel({ xFraction: line.x1, yValue: line.y1 });
        const p2 = toPixel({ xFraction: line.x2, yValue: line.y2 });
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(p1.px, p1.py);
        ctx.lineTo(p2.px, p2.py);
        ctx.strokeStyle = lineColor;
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.stroke();
        ctx.restore();
      });

      // Draw pending first-point indicator
      if (pendingTrendPointRef.current) {
        const p = toPixel(pendingTrendPointRef.current);
        ctx.save();
        ctx.beginPath();
        ctx.arc(p.px, p.py, 5, 0, 2 * Math.PI);
        ctx.fillStyle = lineColor;
        ctx.fill();
        ctx.restore();
      }
    },
  };

  const handleMAChartClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!trendDrawingMode || !maChartRef.current) return;
    const chart = maChartRef.current;
    const area = chart.chartArea;
    if (!area) return;
    const canvas = e.currentTarget.querySelector('canvas');
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const pixelX = e.clientX - rect.left;
    const pixelY = e.clientY - rect.top;
    // Clamp to chart area
    const clampedX = Math.max(area.left, Math.min(area.right, pixelX));
    const clampedY = Math.max(area.top, Math.min(area.bottom, pixelY));
    const xFraction = (clampedX - area.left) / (area.right - area.left);
    const yValue = chart.scales.y.getValueForPixel(clampedY);

    if (!pendingTrendPointRef.current) {
      pendingTrendPointRef.current = { xFraction, yValue };
      setTrendAwaitingSecond(true);
      chart.update('none');
    } else {
      drawnTrendLinesRef.current = [
        ...drawnTrendLinesRef.current,
        { x1: pendingTrendPointRef.current.xFraction, y1: pendingTrendPointRef.current.yValue, x2: xFraction, y2: yValue },
      ];
      pendingTrendPointRef.current = null;
      setTrendAwaitingSecond(false);
      setTrendLineCount(c => c + 1);
      chart.update('none');
    }
  };

  const maChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: handleVerticalLineClick,
    interaction: {
        mode: 'index' as const,
        intersect: true,
    },
    plugins: {
        legend: {
        position: 'top' as const,
        labels: {
            color: '#9CA3AF',
            usePointStyle: true,
        },
        },
        tooltip: {
        mode: 'index' as const,
        intersect: true,
        },
    },
    scales: {
        x: {
        ticks: {
            color: '#9CA3AF',
        },
        grid: {
            color: 'rgba(156, 163, 175, 0.1)',
        },
        },
        y: {
        type: 'linear' as const,
        position: 'right' as const,
        ticks: {
            color: '#9CA3AF',
            callback: (v: number | string) => `$${Number(v).toFixed(2)}`,
        },
        grid: {
            color: 'rgba(156, 163, 175, 0.1)',
        },
        },
    },
  };

  // Show upgrade page if user has hit their limit
  if (user && limitData) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-800 transition-colors duration-200">
        <NavBar currentPage="technical-analysis" user={user} onLogout={handleLogout} />
        <UpgradeRequired
          feature="Technical Analysis"
          currentTier={user.subscription_tier}
          limitReached={true}
          currentUsage={limitData.currentUsage}
          maxUsage={limitData.maxUsage}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-800 transition-colors duration-200">
      {/* SEO — React 19 hoists to <head> */}
      <title>Technical Analysis — NWC-Analytics</title>
      <meta name="description" content="Interactive candlestick charts with RSI, MACD, Bollinger Bands, and 15+ technical indicators." />
      <link rel="canonical" href="https://nwc-analytics.com/technical-analysis" />
      <NavBar currentPage="technical-analysis" user={user} onLogout={handleLogout} />

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Warrant Warning Box */}
        {isWarrant && (
          <div className="bg-orange-50 dark:bg-orange-900/30 border-2 border-orange-500 dark:border-orange-600 rounded-lg p-6 mb-6">
            <div className="flex items-start gap-3">
              <div className="text-3xl">⚠️</div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-orange-900 dark:text-orange-200 mb-2">
                  Caution: Technical Analysis on Warrant Securities
                </h3>
                <div className="text-orange-800 dark:text-orange-300 space-y-2">
                  <p className="font-medium">
                    You're analyzing a <strong>warrant</strong>, not common stock. Technical indicators may behave differently.
                  </p>
                  <div className="space-y-1.5 text-sm">
                    <p><strong>Important considerations for warrant technical analysis:</strong></p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li><strong>Higher Volatility:</strong> Warrants are typically much more volatile than the underlying stock</li>
                      <li><strong>Time Decay:</strong> Warrants lose value as they approach expiration (theta decay)</li>
                      <li><strong>Lower Liquidity:</strong> Often have wider bid-ask spreads and lower trading volume</li>
                      <li><strong>Leverage Effect:</strong> Price movements are amplified relative to the underlying stock</li>
                      <li><strong>Expiration Risk:</strong> Becomes worthless if not exercised before expiration date</li>
                    </ul>
                    
                    <div className="mt-3 p-3 bg-orange-100 dark:bg-orange-900/50 rounded border border-orange-300 dark:border-orange-700">
                      <p className="font-semibold mb-1">Recommended Actions:</p>
                      <ul className="list-disc list-inside ml-4 space-y-1">
                        <li>Always check the <strong>expiration date</strong> before trading</li>
                        <li>Compare warrant price movement to the <strong>underlying common stock</strong></li>
                        <li>Calculate <strong>intrinsic value</strong> (Stock Price - Strike Price)</li>
                        <li>Monitor <strong>time value</strong> remaining (Warrant Price - Intrinsic Value)</li>
                      </ul>
                      {relatedCommonStock && (
                        <div className="mt-3">
                          <p className="font-semibold mb-1">Analyze the underlying stock:</p>
                          <Link
                            to={`/technical-analysis?ticker=${relatedCommonStock}`}
                            className="inline-block px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors"
                          >
                            View {relatedCommonStock} Technical Analysis →
                          </Link>
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-3 p-3 bg-blue-100 dark:bg-blue-900/30 rounded border border-blue-300 dark:border-blue-700">
                      <p className="font-semibold text-blue-900 dark:text-blue-200 mb-1">Warrant-Specific Indicators to Watch:</p>
                      <ul className="list-disc list-inside ml-4 space-y-1 text-blue-800 dark:text-blue-300">
                        <li><strong>RSI:</strong> May hit extreme levels more frequently due to high volatility</li>
                        <li><strong>Bollinger Bands:</strong> Expect wider bands and more frequent band touches</li>
                        <li><strong>Volume:</strong> Critical to watch - low volume = higher risk</li>
                        <li><strong>Moving Averages:</strong> Consider shorter-term MAs (5-day, 10-day) due to volatility</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analysis Form */}
        <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 mb-6 border dark:border-gray-500">
          <form onSubmit={handleAnalyze} className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Stock Ticker {isWarrant && <span className="text-orange-600 dark:text-orange-400 text-xs ml-2">(WARRANT - Use caution)</span>}
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={ticker}
                  onChange={(e) => handleTickerChange(e.target.value.toUpperCase())}
                  placeholder="e.g., AAPL, TSLA, MSFT"
                  className="w-full px-4 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                />
                {/* Clear X inside input */}
                {ticker && (
                  <button
                    type="button"
                    onClick={() => {
                      setTicker('');
                      setAnalysisData(null);
                      setError('');
                      setIsWarrant(false);
                      setRelatedCommonStock(null);
                      setFinancialsData(null);
                      setShowFinancials(false);
                      setFinancialsError(null);
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                    title="Clear search"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-end gap-2">
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
              >
                {loading ? 'Analyzing...' : '📈 Analyze'}
              </button>
              {analysisData && (
                <button
                  type="button"
                  onClick={() => {
                    setTicker('');
                    setAnalysisData(null);
                    setError('');
                    setIsWarrant(false);
                    setRelatedCommonStock(null);
                    setFinancialsData(null);
                    setShowFinancials(false);
                    setFinancialsError(null);
                  }}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 rounded-lg font-medium transition-colors text-sm"
                  title="Clear results and search a new ticker"
                >
                  🔄 New Search
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Analysis Results */}
        {analysisData && (
          <div className="space-y-6">
            {/* Summary Card */}
            <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {analysisData.company_name} ({analysisData.ticker})
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Analysis as of {new Date(`${analysisData.analysis_date.split('T')[0].split(' ')[0]}T00:00:00`).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    ${analysisData.current_price?.toFixed(2) ?? '—'}
                  </div>
                </div>
              </div>

              {/* Quick Actions: Watchlist + Cross-page links */}
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <button
                  onClick={handleAiAnalysis}
                  disabled={aiLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {aiLoading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <span className="text-base leading-none">✦</span>
                      AI Analysis
                    </>
                  )}
                </button>
                <button
                  onClick={handleAddToWatchlist}
                  disabled={addingToWatchlist}
                  className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 dark:bg-yellow-600 dark:hover:bg-yellow-700 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {addingToWatchlist ? '⏳ Adding...' : '⭐ Add to Watchlist'}
                </button>
                <Link
                  to={`/stocks?ticker=${analysisData.ticker}`}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white rounded-lg font-medium text-sm transition-colors"
                >
                  📊 Stock Details
                </Link>
                <Link
                  to={`/dcf-valuation?ticker=${analysisData.ticker}`}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white rounded-lg font-medium text-sm transition-colors"
                >
                  💰 DCF Valuation
                </Link>
                <button
                  onClick={handleFetchFinancials}
                  disabled={financialsLoading}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {financialsLoading ? '⏳ Loading...' : showFinancials && financialsData ? '📄 Hide Financials' : '📄 Financial Summary'}
                </button>
                <button
                  onClick={() => document.getElementById('advanced-indicators')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  className="px-4 py-2 bg-teal-600 hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-600 text-white rounded-lg font-medium text-sm transition-colors"
                >
                  📈 Advanced Indicators
                </button>
                {watchlistMsg && (
                  <span className={`text-sm font-medium ${watchlistMsg.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {watchlistMsg.text}
                  </span>
                )}
              </div>

              {/* AI Analysis Summary */}
              {aiSummary && (
                <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-indigo-600 dark:text-indigo-400 text-base">✦</span>
                        <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wide">AI-generated summary</span>
                        {aiGeneratedAt && (
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            · {new Date(aiGeneratedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{aiSummary}</p>
                    </div>
                    <button
                      onClick={handleAiAnalysis}
                      disabled={aiLoading}
                      className="flex-shrink-0 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      Refresh
                    </button>
                  </div>
                </div>
              )}

              {/* Outlook Summary */}
              <div className={`p-4 rounded-lg ${
                analysisData.summary.outlook === 'bullish'
                  ? 'bg-green-50 dark:bg-green-900/30 border border-green-500 dark:border-green-600'
                  : analysisData.summary.outlook === 'bearish'
                  ? 'bg-red-50 dark:bg-red-900/30 border border-red-500 dark:border-red-600'
                  : 'bg-gray-50 dark:bg-gray-600 border border-gray-400 dark:border-gray-500'
              }`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">
                      {analysisData.summary.outlook === 'bullish' ? '🟢' :
                       analysisData.summary.outlook === 'bearish' ? '🔴' : '🟡'}
                    </span>
                    <div>
                      <div className="font-bold text-lg capitalize text-gray-900 dark:text-white">
                        {analysisData.summary.outlook} Outlook
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                        {analysisData.summary.message}
                      </p>
                    </div>
                  </div>
                  {analysisData.summary.price_range && (
                    <div className="text-right flex-shrink-0">
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Technical Range
                      </div>
                      <div className="text-lg font-bold text-gray-900 dark:text-white mt-0.5">
                        ${analysisData.summary.price_range.support.toFixed(2)} – ${analysisData.summary.price_range.resistance.toFixed(2)}
                      </div>
                      <div className="text-xs italic text-gray-500 dark:text-gray-400 mt-0.5">
                        Indicator-derived range, not a price target
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── Financial Summary Panel ────────────────────────── */}
            {showFinancials && (
              <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                  📄 Financial Summary — {analysisData.ticker}
                </h3>
                {/* The provenance line is itself a trust claim, so it is
                    withheld along with the figures when the vendor has attached
                    the wrong company's filings to this ticker. */}
                {!isEntityContradicted(financialsData?.entity_trust) && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    SEC filings via Financial Modeling Prep • Data updates daily
                    {/* Figures are dollars; for a foreign filer say they were
                        converted, or they won't match the company's own filings. */}
                    {financialsData?.usd_conversion && (
                      financialsData.usd_conversion.rate != null ? (
                        <> • Converted from {financialsData.usd_conversion.from} to US dollars at
                          today's rate ({financialsData.usd_conversion.rate.toLocaleString('en-US', { maximumFractionDigits: 4 })} {financialsData.usd_conversion.from} per USD)</>
                      ) : (
                        <> • Reported in {financialsData.usd_conversion.from}; figures withheld until a
                          US dollar exchange rate is available</>
                      )
                    )}
                  </p>
                )}

                {financialsData?.entity_trust && (
                  <EntityTrustBlock trust={financialsData.entity_trust} className="mb-2" />
                )}

                {financialsLoading && (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
                    <span className="ml-3 text-gray-600 dark:text-gray-300">Loading financial data...</span>
                  </div>
                )}

                {financialsError && (
                  <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
                    {financialsError}
                  </div>
                )}

                {financialsData && !financialsLoading && !isEntityContradicted(financialsData.entity_trust) && (
                  <div className="space-y-6">

                    {/* ── AI Financial Read ──────────────────────────────
                        Sits above the grids on purpose: the figures it discusses
                        are directly below it. Renders nothing when the backend
                        suppresses (fund, warrant, stale filings, bankruptcy,
                        nothing notable) — absence is the right message there. */}
                    {!finAiSuppressed && (
                      <div>
                        {finAiSummary ? (
                          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-indigo-600 dark:text-indigo-400 text-base">✦</span>
                                  <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wide">AI financial read</span>
                                  {finAiGeneratedAt && (
                                    <span className="text-xs text-gray-400 dark:text-gray-500">
                                      · {new Date(finAiGeneratedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{finAiSummary}</p>
                              </div>
                              <button
                                onClick={() => handleFinancialsAi(true)}
                                disabled={finAiLoading}
                                className="flex-shrink-0 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                              >
                                Refresh
                              </button>
                            </div>
                          </div>
                        ) : finAiError ? (
                          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 rounded-lg px-4 py-3 text-sm flex items-center justify-between gap-4">
                            <span>{finAiError}</span>
                            <button
                              onClick={() => handleFinancialsAi(false)}
                              disabled={finAiLoading}
                              className="flex-shrink-0 text-xs font-medium underline disabled:opacity-50 whitespace-nowrap"
                            >
                              Try again
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleFinancialsAi(false)}
                            disabled={finAiLoading}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {finAiLoading ? (
                              <>
                                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                Analyzing...
                              </>
                            ) : (
                              <>
                                <span className="text-base leading-none">✦</span>
                                Generate AI read of these financials
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    )}

                    {/* ── Valuation & Ratios Row ── */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Valuation & Ratios</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                        {[
                          { label: 'P/E', value: fmtNum(financialsData.ratios?.pe_ratio) },
                          { label: 'PEG', value: fmtNum(financialsData.ratios?.peg_ratio) },
                          { label: 'EV/EBITDA', value: fmtNum(financialsData.ratios?.ev_to_ebitda) },
                          { label: 'P/S', value: fmtNum(financialsData.ratios?.ps_ratio) },
                          { label: 'P/B', value: fmtNum(financialsData.ratios?.pb_ratio) },
                          { label: 'P/FCF', value: fmtNum(financialsData.ratios?.price_to_fcf) },
                          { label: 'EV/Sales', value: fmtNum(financialsData.ratios?.ev_to_sales) },
                        ].map((item) => (
                          <div key={item.label} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
                            <div className="text-xs text-gray-500 dark:text-gray-400">{item.label}</div>
                            <div className="text-lg font-bold text-gray-900 dark:text-white">{item.value}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* ── Profitability Row ── */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Profitability (TTM)</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                        {[
                          { label: 'Revenue', value: fmtB(financialsData.income_statement?.revenue) },
                          { label: 'Gross Margin', value: fmtPct(financialsData.income_statement?.gross_margin_pct) },
                          { label: 'Op. Margin', value: fmtPct(financialsData.income_statement?.operating_margin_pct) },
                          { label: 'Net Margin', value: fmtPct(financialsData.income_statement?.net_margin_pct) },
                          { label: 'ROE', value: financialsData.ratios?.roe != null ? `${(financialsData.ratios.roe * 100).toFixed(1)}%` : '—' },
                          { label: 'ROA', value: financialsData.ratios?.roa != null ? `${(financialsData.ratios.roa * 100).toFixed(1)}%` : '—' },
                        ].map((item) => (
                          <div key={item.label} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
                            <div className="text-xs text-gray-500 dark:text-gray-400">{item.label}</div>
                            <div className="text-lg font-bold text-gray-900 dark:text-white">{item.value}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* ── Financial Health Row ── */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Financial Health</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                        {[
                          { label: 'Cash & ST Inv.', value: fmtB(liquidAssets(financialsData.balance_sheet)) },
                          // total_debt, not long_term_debt — the card was labelled
                          // "Total Debt" while showing only the long-term portion,
                          // which would visibly contradict the AI read above it.
                          { label: 'Total Debt', value: fmtB(financialsData.balance_sheet?.total_debt) },
                          { label: 'Total Equity', value: fmtB(financialsData.balance_sheet?.total_equity) },
                          { label: 'D/E Ratio', value: fmtNum(financialsData.ratios?.debt_to_equity) },
                          { label: 'Current Ratio', value: fmtNum(financialsData.ratios?.current_ratio) },
                          { label: 'Quick Ratio', value: fmtNum(financialsData.ratios?.quick_ratio) },
                        ].map((item) => (
                          <div key={item.label} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
                            <div className="text-xs text-gray-500 dark:text-gray-400">{item.label}</div>
                            <div className="text-lg font-bold text-gray-900 dark:text-white">{item.value}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* ── Cash Flow Row ── */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Cash Flow (TTM)</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                        {[
                          { label: 'Operating CF', value: fmtB(financialsData.cash_flow?.operating_cash_flow) },
                          { label: 'CapEx', value: fmtB(financialsData.cash_flow?.capex) },
                          { label: 'Free Cash Flow', value: fmtB(financialsData.cash_flow?.free_cash_flow) },
                          { label: 'Dividends', value: fmtB(financialsData.cash_flow?.dividends) },
                          { label: 'D&A', value: fmtB(financialsData.cash_flow?.depreciation_amortization) },
                        ].map((item) => (
                          <div key={item.label} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
                            <div className="text-xs text-gray-500 dark:text-gray-400">{item.label}</div>
                            <div className="text-lg font-bold text-gray-900 dark:text-white">{item.value}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* ── Quarterly Revenue Trend ── */}
                    {financialsData.quarterly_trend && financialsData.quarterly_trend.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Quarterly Trend</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-600">
                                <th className="pb-2 pr-4 w-px whitespace-nowrap">Quarter</th>
                                <th className="pb-2 pr-4 text-right">Revenue</th>
                                <th className="pb-2 pr-4 text-right">Net Income</th>
                                <th className="pb-2 pr-4 text-right">Gross Margin</th>
                                <th className="pb-2 pr-4 text-right">Op. Margin</th>
                                <th className="pb-2 pr-4 text-right">EPS</th>
                                <th className="pb-2 text-right">P/E</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[...financialsData.quarterly_trend].map((q: any, i: number) => (
                                <tr key={i} className="border-b border-gray-100 dark:border-gray-700 text-gray-900 dark:text-gray-200">
                                  <td className="py-2 pr-4 font-medium whitespace-nowrap">
                                    {q.fiscal_year ? `FY${q.fiscal_year} ${q.fiscal_quarter}` : q.period_end}
                                  </td>
                                  <td className="py-2 pr-4 text-right">{fmtB(q.revenue)}</td>
                                  <td className="py-2 pr-4 text-right">{fmtB(q.net_income)}</td>
                                  <td className="py-2 pr-4 text-right">{fmtPct(q.gross_margin_pct)}</td>
                                  <td className="py-2 pr-4 text-right">{fmtPct(q.operating_margin_pct)}</td>
                                  <td className="py-2 pr-4 text-right">{formatUsd(q.eps_diluted, { compact: false })}</td>
                                  <td className="py-2 text-right whitespace-nowrap">
                                    {q.pe_ratio != null ? `${fmtNum(q.pe_ratio, 1)}x` : (
                                      <span
                                        className="text-xs text-gray-400 dark:text-gray-500"
                                        title={q.pe_status === 'negative_earnings'
                                          ? 'Trailing 12-month EPS was zero or negative, so a P/E multiple is not meaningful.'
                                          : 'Price or a full four quarters of EPS was unavailable for this period.'}
                                      >
                                        {q.pe_status === 'negative_earnings' ? 'Negative Earnings' : 'No Data'}
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* ── DCF-Ready Suggestions ── */}
                    {financialsData.dcf_suggestions && (
                      <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4 border border-indigo-200 dark:border-indigo-800">
                        <h4 className="text-sm font-semibold text-indigo-700 dark:text-indigo-300 uppercase tracking-wider mb-3">
                          💡 Derived DCF Inputs (from Actuals)
                        </h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                          These values replace sector-default assumptions with company-specific data. Use them to pre-populate your DCF model.
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                          {[
                            { label: 'YoY Rev. Growth', value: fmtPct(financialsData.dcf_suggestions.revenue_growth_yoy_pct) },
                            { label: 'Suggested Growth', value: fmtPct(financialsData.dcf_suggestions.suggested_growth_rate) },
                            { label: 'Op. Margin', value: fmtPct(financialsData.dcf_suggestions.operating_margin_pct) },
                            { label: 'Est. WACC', value: fmtPct(financialsData.dcf_suggestions.estimated_wacc) },
                            { label: 'TTM FCF', value: fmtB(financialsData.dcf_suggestions.fcf_ttm) },
                          ].map((item) => (
                            <div key={item.label} className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center">
                              <div className="text-xs text-gray-500 dark:text-gray-400">{item.label}</div>
                              <div className="text-lg font-bold text-indigo-700 dark:text-indigo-300">{item.value}</div>
                            </div>
                          ))}
                        </div>
                        <div className="mt-3 text-right">
                          <Link
                            to={`/dcf-valuation?ticker=${analysisData.ticker}&from=ta${
                              financialsData.dcf_suggestions.suggested_growth_rate != null ? `&growth=${financialsData.dcf_suggestions.suggested_growth_rate}` : ''
                            }${
                              financialsData.dcf_suggestions.estimated_wacc != null ? `&wacc=${financialsData.dcf_suggestions.estimated_wacc}` : ''
                            }${
                              financialsData.dcf_suggestions.fcf_ttm != null ? `&fcf=${financialsData.dcf_suggestions.fcf_ttm}` : ''
                            }${
                              financialsData.dcf_suggestions.revenue_growth_yoy_pct != null ? `&revgrowth=${financialsData.dcf_suggestions.revenue_growth_yoy_pct}` : ''
                            }${
                              financialsData.dcf_suggestions.operating_margin_pct != null ? `&opmarg=${financialsData.dcf_suggestions.operating_margin_pct}` : ''
                            }${
                              financialsData.dcf_suggestions.debt_to_equity != null ? `&de=${financialsData.dcf_suggestions.debt_to_equity}` : ''
                            }`}
                            className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                          >
                            Open DCF Valuation with these inputs →
                          </Link>
                        </div>
                      </div>
                    )}

                    {/* Market cap & EV footer */}
                    <div className="flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-600">
                      {financialsData.ratios?.market_cap && (
                        <span>Market Cap: <strong className="text-gray-700 dark:text-gray-300">{fmtB(financialsData.ratios.market_cap)}</strong></span>
                      )}
                      {financialsData.ratios?.enterprise_value && (
                        <span>Enterprise Value: <strong className="text-gray-700 dark:text-gray-300">{fmtB(financialsData.ratios.enterprise_value)}</strong></span>
                      )}
                      {financialsData.ratios?.dividend_yield != null && financialsData.ratios.dividend_yield > 0 && (
                        <span>Dividend Yield: <strong className="text-gray-700 dark:text-gray-300">{(financialsData.ratios.dividend_yield * 100).toFixed(2)}%</strong></span>
                      )}
                      {financialsData.ratios?.eps && (
                        <span>EPS (TTM): <strong className="text-gray-700 dark:text-gray-300">{formatUsd(financialsData.ratios.eps, { compact: false })}</strong></span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Trading Signals */}
            {analysisData.signals && analysisData.signals.length > 0 && (
              <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Trading Signals</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  The outlook above is scored from the <span className="text-teal-600 dark:text-teal-400 font-medium">teal-bordered</span> indicators (trend indicators count double). Cards without a border — ROC, A/D Line, Keltner, Donchian, ATR — are informational and don't affect the score.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {analysisData.signals.map((signal, index) => (
                    <div key={index} className={`flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                      signal.scored
                        ? 'border border-teal-500 dark:border-teal-600'
                        : 'border border-transparent'
                    }`} onClick={() => scrollToChart(signal.indicator)}>
                      <span className={`flex-shrink-0 px-2 py-1 rounded text-xs font-bold text-center min-w-[64px] text-white ${signalPillClass(signal.type)}`}>
                        {signal.type.charAt(0).toUpperCase() + signal.type.slice(1)}
                      </span>
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 dark:text-white">{signal.indicator}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">{signal.message}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bollinger Bands + Volume Chart */}
            <div id="chart-bollinger" className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-4">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Bollinger Bands &amp; Volume</h3>
                  {renderChartSignal('chart-bollinger')}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    📊 <strong>How to read:</strong> Green dashed line = Lower Bollinger Band,
                    Red dashed line = Upper Bollinger Band; the bands widen and narrow with volatility.
                    The middle band (not shown) is the 20-day SMA — in a range price tends to revert toward it,
                    but in a trend price can ride the upper or lower band, so a band touch isn't a buy or sell on its own.
                    Volume bars on the right axis show trading activity.
                </p>
                {isWarrant && (
                  <p className="text-sm text-orange-600 dark:text-orange-400 mb-4 font-medium">
                    ⚠️ <strong>Warrant Note:</strong> Expect higher volatility and more frequent Bollinger Band touches than common stocks.
                  </p>
                )}
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    <strong>Situations to Look For:</strong> When the bands squeeze tight, volatility is contracting. Traders watch 
                    for it to expand again; the squeeze itself doesn't indicate direction or guarantee a move.
                </p>
                <div className="relative" style={{ height: '300px' }}>
                    {clearLineButton}
                    {getBBChartData() && (
                    <Chart type="line" data={getBBChartData()!} options={priceChartOptions} plugins={[verticalLinePlugin]} />
                    )}
                </div>
            </div>

            {/* Indicator Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Moving Averages */}
              <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Moving Averages</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">20-day SMA:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      ${analysisData.indicators.moving_averages.sma_20?.toFixed(2) || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">50-day SMA:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      ${analysisData.indicators.moving_averages.sma_50?.toFixed(2) || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">200-day SMA:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      ${analysisData.indicators.moving_averages.sma_200?.toFixed(2) || 'N/A'}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
                  {analysisData.indicators.moving_averages.description}
                </p>
              </div>

              {/* Bollinger Bands */}
              <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">Bollinger Bands</h3>
                  {analysisData.indicators.squeeze && analysisData.indicators.squeeze.state !== 'none' && (
                    analysisData.indicators.squeeze.state === 'on' ? (
                      <span className="px-2 py-1 text-xs font-bold rounded bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">
                        Squeeze ON · {analysisData.indicators.squeeze.bars_in_squeeze} bar{analysisData.indicators.squeeze.bars_in_squeeze === 1 ? '' : 's'}
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-bold rounded bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300">
                        Squeeze Fired · {analysisData.indicators.squeeze.bars_since_fire === 0 ? 'today' : `${analysisData.indicators.squeeze.bars_since_fire}d ago`}
                      </span>
                    )
                  )}
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">Upper Band:</span>
                    <span className="font-semibold text-red-500">
                      ${analysisData.indicators.bollinger_bands.upper_band?.toFixed(2) ?? 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">Middle Band:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      ${analysisData.indicators.bollinger_bands.middle_band?.toFixed(2) ?? 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">Lower Band:</span>
                    <span className="font-semibold text-green-500">
                      ${analysisData.indicators.bollinger_bands.lower_band != null ? Math.max(0, analysisData.indicators.bollinger_bands.lower_band).toFixed(2) : 'N/A'}
                    </span>
                  </div>
                  <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Position: </span>
                      <span className="font-semibold text-gray-900 dark:text-white capitalize">
                        {analysisData.indicators.bollinger_bands.position.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
                  {analysisData.indicators.bollinger_bands.description}
                </p>
              </div>

              {/* Keltner Channels */}
              <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Keltner Channels</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">Upper Channel:</span>
                    <span className="font-semibold text-red-500">
                      ${analysisData.indicators.keltner?.upper?.toFixed(2) ?? 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">Middle (EMA):</span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      ${analysisData.indicators.keltner?.middle?.toFixed(2) ?? 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">Lower Channel:</span>
                    <span className="font-semibold text-green-500">
                      ${analysisData.indicators.keltner?.lower != null ? Math.max(0, analysisData.indicators.keltner.lower).toFixed(2) : 'N/A'}
                    </span>
                  </div>
                  <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Position: </span>
                      <span className="font-semibold text-gray-900 dark:text-white capitalize">
                        {analysisData.indicators.keltner?.position?.replace('_', ' ') ?? 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
                  {analysisData.indicators.keltner?.description ?? 'Keltner Channels — EMA ± ATR volatility envelope around the 20-day EMA.'}
                </p>
              </div>
            </div>

            {/* Moving Averages Chart with Golden/Death Cross */}
            <div id="chart-moving-averages" className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-4">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Moving Averages — Trend &amp; Crossovers</h3>
                  {renderChartSignal('chart-moving-averages')}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    📊 <strong>How to read:</strong> Yellow line = 20-day SMA (short-term trend), 
                    Purple line = 50-day SMA (medium-term trend). 
                    When the price stays above both lines, the trend is bullish. Below both = bearish. 
                    Between them = indecision or transition.
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    <strong>Golden Cross &amp; Death Cross:</strong> When the 20-day SMA crosses <em>above</em> the 50-day SMA, 
                    it forms a <strong className="text-green-500">Golden Cross</strong> — a bullish signal indicating short-term momentum 
                    is outpacing the medium-term trend. When the 20-day crosses <em>below</em> the 50-day, 
                    it forms a <strong className="text-red-500">Death Cross</strong> — a bearish signal.
                    Because both lines are built from past prices, these crosses lag the move and whipsaw in choppy markets —
                    look for the cross, then confirm with volume and RSI before acting.
                </p>
                {isWarrant && (
                  <p className="text-sm text-orange-600 dark:text-orange-400 mb-4 font-medium">
                    ⚠️ <strong>Warrant Note:</strong> MA crossovers on warrants can produce more false signals due to higher volatility. Confirm with volume and broader market context.
                  </p>
                )}
                <div className="relative" style={{ height: '300px' }}>
                  {clearLineButton}
                  {/* Trend line controls — overlaid at top-right, flush with the legend row */}
                  <div className="absolute top-0 right-0 z-10 flex items-center gap-2" style={{ marginTop: '2px' }}>
                    <button
                      onClick={() => {
                        setTrendDrawingMode(m => !m);
                        pendingTrendPointRef.current = null;
                        setTrendAwaitingSecond(false);
                        if (maChartRef.current) maChartRef.current.update('none');
                      }}
                      className={`px-2 py-0.5 text-xs font-medium rounded border transition-colors ${
                        trendDrawingMode
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-500'
                      }`}
                    >
                      {trendDrawingMode
                        ? trendAwaitingSecond ? 'Click second point…' : 'Click first point…'
                        : 'Draw Trend Line'}
                    </button>
                    {trendLineCount > 0 && (
                      <button
                        onClick={() => {
                          drawnTrendLinesRef.current = [];
                          pendingTrendPointRef.current = null;
                          setTrendAwaitingSecond(false);
                          setTrendLineCount(0);
                          if (maChartRef.current) maChartRef.current.update('none');
                        }}
                        className="px-2 py-0.5 text-xs font-medium rounded border bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div
                    style={{ height: '100%', cursor: trendDrawingMode ? 'crosshair' : 'default' }}
                    onClick={handleMAChartClick}
                  >
                    {getMAChartData() && (
                      <Chart
                        ref={maChartRef}
                        type="line"
                        data={getMAChartData()!}
                        options={maChartOptions}
                        plugins={[trendLinePlugin, verticalLinePlugin]}
                      />
                    )}
                  </div>
                </div>
            </div>

            {/* RSI Chart */}
            <div id="chart-rsi" className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">RSI (Relative Strength Index)</h3>
                  {renderChartSignal('chart-rsi')}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Current RSI: <strong className={analysisData.indicators.rsi.value && analysisData.indicators.rsi.value < 30 ? 'text-green-500' : analysisData.indicators.rsi.value && analysisData.indicators.rsi.value > 70 ? 'text-red-500' : 'text-gray-900 dark:text-white'}>
                    {analysisData.indicators.rsi.value?.toFixed(2) || 'N/A'}
                    </strong> - {analysisData.indicators.rsi.description}
                </p>
                {isWarrant && (
                  <p className="text-sm text-orange-600 dark:text-orange-400 mb-4 font-medium">
                    ⚠️ <strong>Warrant Note:</strong> Warrants often hit extreme RSI levels (below 30 or above 70) more frequently than common stocks.
                  </p>
                )}
                <div className="relative" style={{ height: '300px' }}>
                    {clearLineButton}
                    {getRSIChartData() && (
                    <Line
                        data={getRSIChartData()!}
                        plugins={[verticalLinePlugin]}
                        options={{
                        ...chartOptions, 
                        scales: {
                            ...chartOptions.scales, 
                            y: {
                            min: 0, 
                            max: 100, 
                            ticks: {color: '#9CA3AF'}, 
                            grid: {color: 'rgba(156, 163, 175, 0.1)'}
                            }
                        }
                        }} 
                    />
                    )}
                </div>
            </div>

            {/* MACD Chart */}
            <div id="chart-macd" className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">MACD (Moving Average Convergence Divergence)</h3>
                {renderChartSignal('chart-macd')}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Current Trend: <strong className={analysisData.indicators.macd.trend === 'bullish' ? 'text-green-500' : 'text-red-500'}>
                  {analysisData.indicators.macd.trend}
                </strong> - {analysisData.indicators.macd.description}
              </p>
              <div className="relative" style={{ height: '300px' }}>
                {clearLineButton}
                {getMACDChartData() && (
                    <Chart type="bar" data={getMACDChartData()!} options={chartOptions} plugins={[verticalLinePlugin]} />
                )}
              </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* ADVANCED INDICATORS — Toggleable Panels                    */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <div id="advanced-indicators" className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">📊 Advanced Indicators</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Click a category to expand charts and details</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { key: 'volume', label: '📊 Volume', state: showVolume, toggle: setShowVolume, color: 'blue' },
                  { key: 'momentum', label: '⚡ Momentum', state: showMomentum, toggle: setShowMomentum, color: 'purple' },
                  { key: 'volatility', label: '🌊 Volatility', state: showVolatility, toggle: setShowVolatility, color: 'orange' },
                  { key: 'trend', label: '📈 Trend', state: showTrend, toggle: setShowTrend, color: 'green' },
                ].map(cat => (
                  <button
                    key={cat.key}
                    onClick={() => cat.toggle(!cat.state)}
                    className={`p-3 rounded-lg font-semibold text-sm transition-all border-2 ${
                      cat.state 
                        ? 'bg-primary-600 text-white border-primary-600 dark:bg-primary-500 dark:border-primary-500'
                        : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-500 hover:border-primary-400 dark:hover:border-primary-400'
                    }`}
                  >
                    {cat.label} {cat.state ? '▼' : '▶'}
                  </button>
                ))}
              </div>
            </div>

            {/* ── VOLUME INDICATORS ──────────────────────────────────── */}
            {showVolume && (
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">📊 Volume Indicators</h3>
                {/* Volume indicator summary cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* MVWAP card — clickable to open true intraday VWAP */}
                  <div
                    className="bg-white dark:bg-gray-700 rounded-lg shadow dark:shadow-gray-200/20 p-4 border dark:border-gray-500 cursor-pointer hover:ring-2 hover:ring-teal-500 transition-all"
                    onClick={() => setShowTrueVwap(true)}
                  >
                    <h4 className="font-bold text-gray-900 dark:text-white mb-2">MVWAP</h4>
                    {analysisData.indicators.vwap ? (
                      <>
                        <p className={`text-sm font-semibold ${analysisData.indicators.vwap.signal === 'bullish' ? 'text-green-500' : 'text-red-500'}`}>
                          {analysisData.indicators.vwap.signal?.toUpperCase()}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{analysisData.indicators.vwap.description}</p>
                      </>
                    ) : <p className="text-xs text-gray-400">N/A</p>}
                    <p className="text-xs text-teal-500 dark:text-teal-400 mt-2 font-medium">View Session VWAP →</p>
                  </div>
                  {/* OBV + A/D Line cards */}
                  {[
                    { label: 'OBV', data: analysisData.indicators.obv },
                    { label: 'A/D Line', data: analysisData.indicators.ad_line },
                  ].map(item => (
                    <div key={item.label} className="bg-white dark:bg-gray-700 rounded-lg shadow dark:shadow-gray-200/20 p-4 border dark:border-gray-500">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-2">{item.label}</h4>
                      {item.data ? (
                        <>
                          <p className={`text-sm font-semibold ${item.data.signal === 'bullish' ? 'text-green-500' : item.data.signal === 'bearish' ? 'text-red-500' : 'text-gray-500'}`}>
                            {item.data.signal?.toUpperCase()}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{item.data.description}</p>
                        </>
                      ) : <p className="text-xs text-gray-400">N/A</p>}
                    </div>
                  ))}
                </div>
                {/* VWAP Chart */}
                <div id="chart-vwap" className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">MVWAP (Multi-Day VWAP)</h4>
                    {renderChartSignal('chart-vwap')}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    MVWAP (Multi-Day VWAP) accumulates cumulative (price × volume) from the start of the selected date range, giving the volume-weighted average cost basis over the entire period. This is useful for identifying long-term fair value and trend direction. For the true intraday VWAP — which resets each session and is used by institutional traders as a daily benchmark — click the MVWAP card below.
                  </p>
                  <div className="relative" style={{ height: '300px' }}>
                    {clearLineButton}
                    <Line data={{
                      labels: analysisData.chart_data.map(d => d.date),
                      datasets: [
                        { label: 'Price', data: analysisData.chart_data.map(d => d.close), borderColor: 'rgb(59, 130, 246)', borderWidth: 2, pointRadius: 0, tension: 0.1, fill: false },
                        { label: 'MVWAP', data: analysisData.chart_data.map(d => d.vwap), borderColor: 'rgb(245, 158, 11)', borderWidth: 2, pointRadius: 0, tension: 0.1, borderDash: [5,5], fill: false },
                      ]
                    }} options={chartOptions} plugins={[verticalLinePlugin]} />
                  </div>
                </div>
                {/* OBV Chart */}
                <div id="chart-obv" className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">On-Balance Volume (OBV)</h4>
                    {renderChartSignal('chart-obv')}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">OBV tracks cumulative buying and selling pressure by adding volume on up days and subtracting on down days. It's a confirmation tool, not a trigger: a rising OBV supports an uptrend, and a divergence (price rising while OBV falls) can warn the move is weakening — but divergences can persist for a long time before price turns, if it turns at all.</p>
                  <div className="relative" style={{ height: '250px' }}>
                    {clearLineButton}
                    <Line data={{
                      labels: analysisData.chart_data.map(d => d.date),
                      datasets: [
                        { label: 'OBV', data: analysisData.chart_data.map(d => d.obv), borderColor: 'rgb(16, 185, 129)', borderWidth: 2, pointRadius: 0, tension: 0.1, fill: true, backgroundColor: 'rgba(16, 185, 129, 0.1)' },
                      ]
                    }} options={chartOptions} plugins={[verticalLinePlugin]} />
                  </div>
                </div>
                {/* A/D Line Chart */}
                <div id="chart-ad" className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">Accumulation/Distribution Line</h4>
                    {renderChartSignal('chart-ad')}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">The A/D Line refines OBV by weighting each day's volume by where price closes within its range — a close near the high counts as accumulation, near the low as distribution. Like OBV it's a confirmation tool: a rising line supports an uptrend, while a divergence (price rising as the A/D Line falls) can warn the move is losing internal support — though divergences can persist for a long time before price turns, if it turns at all.</p>
                  <div className="relative" style={{ height: '250px' }}>
                    {clearLineButton}
                    <Line data={{
                      labels: analysisData.chart_data.map(d => d.date),
                      datasets: [
                        { label: 'A/D Line', data: analysisData.chart_data.map(d => d.ad_line), borderColor: 'rgb(59, 130, 246)', borderWidth: 2, pointRadius: 0, tension: 0.1, fill: true, backgroundColor: 'rgba(59, 130, 246, 0.1)' },
                      ]
                    }} options={chartOptions} plugins={[verticalLinePlugin]} />
                  </div>
                </div>
              </div>
            )}

            {/* ── MOMENTUM INDICATORS ────────────────────────────────── */}
            {showMomentum && (
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">⚡ Momentum Indicators</h3>
                {/* Momentum summary cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Stochastic', data: analysisData.indicators.stochastic, val: analysisData.indicators.stochastic ? `%K: ${analysisData.indicators.stochastic.k}` : 'N/A' },
                    { label: 'ADX', data: analysisData.indicators.adx, val: analysisData.indicators.adx ? `${analysisData.indicators.adx.adx}` : 'N/A' },
                    { label: 'CCI', data: analysisData.indicators.cci, val: analysisData.indicators.cci?.value?.toFixed(0) || 'N/A' },
                    { label: 'ROC', data: analysisData.indicators.roc, val: analysisData.indicators.roc?.value ? `${analysisData.indicators.roc.value > 0 ? '+' : ''}${analysisData.indicators.roc.value.toFixed(1)}%` : 'N/A' },
                  ].map(item => (
                    <div key={item.label} className="bg-white dark:bg-gray-700 rounded-lg shadow dark:shadow-gray-200/20 p-4 border dark:border-gray-500">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-1">{item.label}</h4>
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold mb-2 ${
                        (item.data as any)?.signal === 'bullish' || (item.data as any)?.signal === 'oversold' || (item.data as any)?.direction === 'bullish'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                          : (item.data as any)?.signal === 'bearish' || (item.data as any)?.signal === 'overbought' || (item.data as any)?.direction === 'bearish'
                          ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                          : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                      }`}>{item.val}</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{(item.data as any)?.description || (item.data as any)?.signal?.toUpperCase() || 'N/A'}</p>
                    </div>
                  ))}
                </div>
                {/* Stochastic Oscillator */}
                <div id="chart-stochastic" className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">Stochastic Oscillator</h4>
                    {renderChartSignal('chart-stochastic')}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    The Stochastic Oscillator compares the closing price to its recent high–low range. In a ranging market, readings above 80 (overbought) or below 20 (oversold) and %K/%D crossovers can flag reversals — but in a strong trend it can stay pinned in the extreme zone while price keeps moving, so an extreme reading isn't a trade on its own. Use it for context and divergences, confirmed by the trend.
                  </p>
                  <div className="relative" style={{ height: '250px' }}>
                    {clearLineButton}
                    <Line data={{
                      labels: analysisData.chart_data.map(d => d.date),
                      datasets: [
                        { label: '%K', data: analysisData.chart_data.map(d => d.stoch_k), borderColor: 'rgb(59, 130, 246)', borderWidth: 2, pointRadius: 0, tension: 0.1, fill: false },
                        { label: '%D', data: analysisData.chart_data.map(d => d.stoch_d), borderColor: 'rgb(239, 68, 68)', borderWidth: 2, pointRadius: 0, tension: 0.1, borderDash: [5,5], fill: false },
                        { label: 'Overbought', data: Array(analysisData.chart_data.length).fill(80), borderColor: 'rgba(239,68,68,0.4)', borderWidth: 1, borderDash: [3,3], pointRadius: 0, fill: false },
                        { label: 'Oversold', data: Array(analysisData.chart_data.length).fill(20), borderColor: 'rgba(34,197,94,0.4)', borderWidth: 1, borderDash: [3,3], pointRadius: 0, fill: false },
                      ]
                    }} options={{...chartOptions, scales: {...chartOptions.scales, y: {...chartOptions.scales.y, min: 0, max: 100}}}} plugins={[verticalLinePlugin]} />
                  </div>
                </div>
                {/* ADX */}
                <div id="chart-adx" className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">ADX (Average Directional Index)</h4>
                    {renderChartSignal('chart-adx')}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    ADX measures trend strength, not direction — above 25 signals a strong trend, below 20 a weak or sideways market. Direction comes from the separate +DI/-DI lines (+DI above -DI is bullish, and vice versa). ADX lags and only tells you whether a trend exists, never whether to buy or sell, so pair it with a direction read before acting.
                  </p>
                  <div className="relative" style={{ height: '250px' }}>
                    {clearLineButton}
                    <Line data={{
                      labels: analysisData.chart_data.map(d => d.date),
                      datasets: [
                        { label: 'ADX', data: analysisData.chart_data.map(d => d.adx), borderColor: 'rgb(139, 92, 246)', borderWidth: 2.5, pointRadius: 0, tension: 0.1, fill: false },
                        { label: '+DI', data: analysisData.chart_data.map(d => d.plus_di), borderColor: 'rgb(34, 197, 94)', borderWidth: 1.5, pointRadius: 0, tension: 0.1, fill: false },
                        { label: '-DI', data: analysisData.chart_data.map(d => d.minus_di), borderColor: 'rgb(239, 68, 68)', borderWidth: 1.5, pointRadius: 0, tension: 0.1, fill: false },
                        { label: 'Trending (25)', data: Array(analysisData.chart_data.length).fill(25), borderColor: 'rgba(156,163,175,0.4)', borderWidth: 1, borderDash: [3,3], pointRadius: 0, fill: false },
                      ]
                    }} options={chartOptions} plugins={[verticalLinePlugin]} />
                  </div>
                </div>
                {/* CCI + ROC */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div id="chart-cci" className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                      <h4 className="text-lg font-bold text-gray-900 dark:text-white">CCI (Commodity Channel Index)</h4>
                      {renderChartSignal('chart-cci')}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">CCI measures how far price has moved from its recent average. In a range, readings above +100 or below −100 mark overbought/oversold extremes that may snap back — but CCI is unbounded and can stay extended through a strong trend, so an extreme reading is context, not a buy or sell signal on its own.</p>
                    <div className="relative" style={{ height: '200px' }}>
                      {clearLineButton}
                      <Line data={{
                        labels: analysisData.chart_data.map(d => d.date),
                        datasets: [
                          { label: 'CCI', data: analysisData.chart_data.map(d => d.cci), borderColor: 'rgb(245, 158, 11)', borderWidth: 2, pointRadius: 0, tension: 0.1, fill: true, backgroundColor: 'rgba(245,158,11,0.1)' },
                          { label: '+100', data: Array(analysisData.chart_data.length).fill(100), borderColor: 'rgba(239,68,68,0.3)', borderWidth: 1, borderDash: [3,3], pointRadius: 0, fill: false },
                          { label: '-100', data: Array(analysisData.chart_data.length).fill(-100), borderColor: 'rgba(34,197,94,0.3)', borderWidth: 1, borderDash: [3,3], pointRadius: 0, fill: false },
                        ]
                      }} options={chartOptions} plugins={[verticalLinePlugin]} />
                    </div>
                  </div>
                  <div id="chart-roc" className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                      <h4 className="text-lg font-bold text-gray-900 dark:text-white">ROC (Rate of Change)</h4>
                      {renderChartSignal('chart-roc')}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">ROC measures the percentage change in price over a set period: above zero is bullish momentum, below zero is bearish. Extreme readings can precede a pullback, but momentum can stay elevated for a long time in a strong trend, so a high ROC alone isn't a reversal signal — use it to gauge momentum and divergences.</p>
                    <div className="relative" style={{ height: '200px' }}>
                      {clearLineButton}
                      <Line data={{
                        labels: analysisData.chart_data.map(d => d.date),
                        datasets: [
                          { label: 'ROC %', data: analysisData.chart_data.map(d => d.roc), borderColor: 'rgb(99, 102, 241)', borderWidth: 2, pointRadius: 0, tension: 0.1, fill: true, backgroundColor: 'rgba(99,102,241,0.1)' },
                          { label: 'Zero', data: Array(analysisData.chart_data.length).fill(0), borderColor: 'rgba(156,163,175,0.4)', borderWidth: 1, borderDash: [3,3], pointRadius: 0, fill: false },
                        ]
                      }} options={chartOptions} plugins={[verticalLinePlugin]} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── VOLATILITY INDICATORS ──────────────────────────────── */}
            {showVolatility && (
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">🌊 Volatility Indicators</h3>
                {/* Volatility summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* ATR card — clickable, opens the stop & position-size calculator */}
                  <div
                    className="bg-white dark:bg-gray-700 rounded-lg shadow dark:shadow-gray-200/20 p-4 border-2 border-teal-500 dark:border-teal-600 cursor-pointer hover:ring-2 hover:ring-teal-500 transition-all"
                    onClick={() => setShowAtrCalc(true)}
                  >
                    <h4 className="font-bold text-gray-900 dark:text-white mb-1">ATR</h4>
                    <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 mb-2">{analysisData.indicators.atr?.volatility || 'N/A'}</span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{analysisData.indicators.atr?.description || 'N/A'}</p>
                    <p className="text-xs text-teal-500 dark:text-teal-400 mt-2 font-medium">Open stop &amp; position-size calculator →</p>
                  </div>
                  {[
                    { label: 'Keltner', desc: analysisData.indicators.keltner?.description, badge: analysisData.indicators.keltner?.position?.replace('_', ' ') },
                    { label: 'Std Dev', desc: analysisData.indicators.std_dev?.description, badge: analysisData.indicators.std_dev?.percent ? `${analysisData.indicators.std_dev.percent}%` : 'N/A' },
                  ].map(item => (
                    <div key={item.label} className="bg-white dark:bg-gray-700 rounded-lg shadow dark:shadow-gray-200/20 p-4 border dark:border-gray-500">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-1">{item.label}</h4>
                      <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 mb-2">{item.badge || 'N/A'}</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{item.desc || 'N/A'}</p>
                    </div>
                  ))}
                </div>
                {/* Chandelier trailing stop (price scale — the ATR chart's axis is in ATR units) */}
                <div id="chart-atr-stop" className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">ATR Trailing Stop (Chandelier Exit)</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">The chandelier exit trails a stop at a fixed ATR multiple from the highest high (long) or lowest low (short) of the last 22 sessions. It widens automatically when volatility rises and ratchets in the direction of the trend without ever moving back. It is a level derived from price and volatility, not a signal — it says nothing about direction and implies nothing about where price will go.</p>
                  <div className="relative" style={{ height: '350px' }}>
                    {clearLineButton}
                    <Line data={{
                      labels: analysisData.chart_data.map(d => d.date),
                      datasets: [
                        { label: 'Price', data: analysisData.chart_data.map(d => d.close), borderColor: 'rgb(59, 130, 246)', borderWidth: 2, pointRadius: 0, tension: 0.1, fill: false },
                        { label: 'Chandelier Long', data: analysisData.chart_data.map(d => d.chandelier_long), borderColor: 'rgb(34, 197, 94)', borderWidth: 1.5, pointRadius: 0, tension: 0.1, borderDash: [4,4], fill: false },
                        { label: 'Chandelier Short', data: analysisData.chart_data.map(d => d.chandelier_short), borderColor: 'rgb(239, 68, 68)', borderWidth: 1.5, pointRadius: 0, tension: 0.1, borderDash: [4,4], fill: false },
                      ]
                    }} options={chartOptions} plugins={[verticalLinePlugin]} />
                  </div>
                </div>
                {/* ATR Chart */}
                <div id="chart-atr" className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">ATR (Average True Range)</h4>
                    {renderChartSignal('chart-atr')}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">ATR measures volatility as the average range between high and low prices over a period — higher ATR means wider swings. It's used to size positions and set stop-losses (avoiding stops too tight in volatile markets or too wide in calm ones). ATR gives no direction and reflects past movement, so it's never a buy or sell signal on its own.</p>
                  <div className="relative" style={{ height: '250px' }}>
                    {clearLineButton}
                    <Line data={{
                      labels: analysisData.chart_data.map(d => d.date),
                      datasets: [
                        { label: 'ATR', data: analysisData.chart_data.map(d => d.atr), borderColor: 'rgb(249, 115, 22)', borderWidth: 2, pointRadius: 0, tension: 0.1, fill: true, backgroundColor: 'rgba(249,115,22,0.1)' },
                      ]
                    }} options={chartOptions} plugins={[verticalLinePlugin]} />
                  </div>
                </div>
                {/* Keltner Channels (overlay on price) */}
                <div id="chart-keltner" className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">Keltner Channels</h4>
                    {renderChartSignal('chart-keltner')}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Keltner Channels plot an EMA with upper and lower bands set by ATR. A close beyond the upper or lower channel points to strong momentum and a possible breakout, while price inside the channels is normal trading — but breakouts can fail and reverse in choppy markets, so confirm with volume or trend rather than acting on the break alone. They're often paired with Bollinger Bands to spot squeeze setups.</p>
                  <div className="relative" style={{ height: '350px' }}>
                    {clearLineButton}
                    <Line data={{
                      labels: analysisData.chart_data.map(d => d.date),
                      datasets: [
                        { label: 'Price', data: analysisData.chart_data.map(d => d.close), borderColor: 'rgb(59, 130, 246)', borderWidth: 2, pointRadius: 0, tension: 0.1, fill: false },
                        { label: 'KC Upper', data: analysisData.chart_data.map(d => d.keltner_upper), borderColor: 'rgb(239, 68, 68)', borderWidth: 1.5, pointRadius: 0, tension: 0.1, borderDash: [4,4], fill: false },
                        { label: 'KC Middle', data: analysisData.chart_data.map(d => d.keltner_middle), borderColor: 'rgb(156, 163, 175)', borderWidth: 1, pointRadius: 0, tension: 0.1, borderDash: [2,2], fill: false },
                        { label: 'KC Lower', data: analysisData.chart_data.map(d => d.keltner_lower), borderColor: 'rgb(34, 197, 94)', borderWidth: 1.5, pointRadius: 0, tension: 0.1, borderDash: [4,4], fill: false },
                      ]
                    }} options={chartOptions} plugins={[verticalLinePlugin]} />
                  </div>
                </div>
              </div>
            )}

            {/* ── TREND INDICATORS ───────────────────────────────────── */}
            {showTrend && (
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">📈 Trend Indicators</h3>
                {/* Trend summary cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { label: 'Parabolic SAR', data: analysisData.indicators.parabolic_sar, badge: analysisData.indicators.parabolic_sar?.trend, color: analysisData.indicators.parabolic_sar?.trend === 'uptrend' ? 'green' : 'red' },
                    { label: 'Ichimoku Cloud', data: analysisData.indicators.ichimoku, badge: analysisData.indicators.ichimoku?.signal, color: analysisData.indicators.ichimoku?.signal === 'bullish' ? 'green' : analysisData.indicators.ichimoku?.signal === 'bearish' ? 'red' : 'gray' },
                    { label: 'Donchian', data: analysisData.indicators.donchian, badge: analysisData.indicators.donchian ? `$${analysisData.indicators.donchian.upper} / $${analysisData.indicators.donchian.lower}` : 'N/A', color: 'gray' },
                  ].map(item => (
                    <div key={item.label} className="bg-white dark:bg-gray-700 rounded-lg shadow dark:shadow-gray-200/20 p-4 border dark:border-gray-500">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-1">{item.label}</h4>
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold mb-2 ${
                        item.color === 'green' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                        : item.color === 'red' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                        : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                      }`}>{item.badge || 'N/A'}</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{item.data?.description || 'N/A'}</p>
                    </div>
                  ))}
                </div>
                {/* Parabolic SAR (dots on price chart) */}
                <div id="chart-parabolic-sar" className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">Parabolic SAR</h4>
                    {renderChartSignal('chart-parabolic-sar')}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Parabolic SAR (Stop and Reverse) places dots below price in an uptrend and above price in a downtrend; a flip to the other side flags a possible trend reversal, which makes it useful for trailing stop-losses. It works well in trending markets but whipsaws badly in sideways, choppy action — generating frequent false flips — so it's best for managing trend exits rather than as a standalone entry trigger.</p>
                  <div className="relative" style={{ height: '350px' }}>
                    {clearLineButton}
                    <Chart type="line" data={{
                      labels: analysisData.chart_data.map(d => d.date),
                      datasets: [
                        { label: 'Price', data: analysisData.chart_data.map(d => d.close), borderColor: 'rgb(59, 130, 246)', borderWidth: 2, pointRadius: 0, tension: 0.1, fill: false, type: 'line' as const },
                        { label: 'SAR', data: analysisData.chart_data.map(d => d.sar), borderColor: 'transparent', borderWidth: 0, pointRadius: 2.5,
                          pointBackgroundColor: analysisData.chart_data.map(d => d.sar_trend === 1 ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)'),
                          fill: false, type: 'line' as const, showLine: false },
                      ]
                    }} options={chartOptions} plugins={[verticalLinePlugin]} />
                  </div>
                </div>
                {/* Ichimoku Cloud */}
                <div id="chart-ichimoku" className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">Ichimoku Cloud</h4>
                    {renderChartSignal('chart-ichimoku')}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">The Ichimoku Cloud shows support/resistance, trend direction, and momentum at once: price above the cloud is bullish, below is bearish, inside is neutral, and Tenkan-Sen/Kijun-Sen crossovers act like moving-average signals. Like other trend tools it lags and produces conflicting, whipsaw-prone signals in flat or choppy markets, so it's most reliable when a clear trend is already in place.</p>
                  <div className="relative" style={{ height: '400px' }}>
                    {clearLineButton}
                    <Line data={{
                      labels: analysisData.chart_data.map(d => d.date),
                      datasets: [
                        { label: 'Price', data: analysisData.chart_data.map(d => d.close), borderColor: 'rgb(59, 130, 246)', borderWidth: 2, pointRadius: 0, tension: 0.1, fill: false },
                        { label: 'Tenkan-sen', data: analysisData.chart_data.map(d => d.ichimoku_tenkan), borderColor: 'rgb(239, 68, 68)', borderWidth: 1.5, pointRadius: 0, tension: 0.1, fill: false },
                        { label: 'Kijun-sen', data: analysisData.chart_data.map(d => d.ichimoku_kijun), borderColor: 'rgb(59, 130, 246)', borderWidth: 1.5, pointRadius: 0, tension: 0.1, borderDash: [5,5], fill: false },
                        { label: 'Senkou A', data: analysisData.chart_data.map(d => d.ichimoku_senkou_a), borderColor: 'rgba(34, 197, 94, 0.6)', borderWidth: 1, pointRadius: 0, tension: 0.1, fill: '+1', backgroundColor: 'rgba(34, 197, 94, 0.08)' },
                        { label: 'Senkou B', data: analysisData.chart_data.map(d => d.ichimoku_senkou_b), borderColor: 'rgba(239, 68, 68, 0.6)', borderWidth: 1, pointRadius: 0, tension: 0.1, fill: false },
                      ]
                    }} options={chartOptions} plugins={[verticalLinePlugin]} />
                  </div>
                </div>
                {/* Donchian Channels */}
                <div id="chart-donchian" className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">Donchian Channels</h4>
                    {renderChartSignal('chart-donchian')}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Donchian Channels plot the highest high and lowest low over a set period to define a breakout system: a break above the upper channel suggests a new uptrend, a break below the lower channel a new downtrend. Made famous by the "Turtle Traders," it shines in trending markets but produces frequent false breakouts in rangebound action, so confirm the break holds before treating it as a signal.</p>
                  <div className="relative" style={{ height: '350px' }}>
                    {clearLineButton}
                    <Line data={{
                      labels: analysisData.chart_data.map(d => d.date),
                      datasets: [
                        { label: 'Price', data: analysisData.chart_data.map(d => d.close), borderColor: 'rgb(59, 130, 246)', borderWidth: 2, pointRadius: 0, tension: 0.1, fill: false },
                        { label: 'DC Upper', data: analysisData.chart_data.map(d => d.donchian_upper), borderColor: 'rgb(34, 197, 94)', borderWidth: 1.5, pointRadius: 0, tension: 0, fill: false },
                        { label: 'DC Middle', data: analysisData.chart_data.map(d => d.donchian_middle), borderColor: 'rgb(156, 163, 175)', borderWidth: 1, pointRadius: 0, tension: 0, borderDash: [3,3], fill: false },
                        { label: 'DC Lower', data: analysisData.chart_data.map(d => d.donchian_lower), borderColor: 'rgb(239, 68, 68)', borderWidth: 1.5, pointRadius: 0, tension: 0, fill: false },
                      ]
                    }} options={chartOptions} plugins={[verticalLinePlugin]} />
                  </div>
                </div>
              </div>
            )}

          </div>
        )}

        {/* Educational Content (shown when no analysis) */}
        {!analysisData && !loading && (
          <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-8 border dark:border-gray-500">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">How to Use Technical Analysis</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">📊 RSI (Relative Strength Index)</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Measures momentum on a 0–100 scale. In a sideways market, dips below 30 (oversold) or pushes above 70 (overbought) can flag a possible reversal — but in a strong trend RSI can stay above 70 (or below 30) for a long time while price keeps running, so it's a momentum gauge, not a buy/sell trigger. It's most reliable for spotting divergences (price makes a new high, RSI doesn't) and for confirming other signals.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">📈 MACD (Moving Average Convergence Divergence)</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Shows trend direction and momentum: the MACD line crossing above the signal line is bullish, crossing below is bearish. It works best confirming a trend that's already underway and whipsaws with false crosses in choppy, sideways markets — confirm with price action or volume before acting.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">📉 Moving Averages</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Average price over time: price above the MA leans bullish, below leans bearish. The 20-day crossing above the 50-day is a Golden Cross (bullish); crossing below is a Death Cross (bearish) — but both are lagging confirmations built from past prices and throw false signals in choppy markets, so confirm before acting.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">🎯 Bollinger Bands</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Bands sit above and below a moving average to show volatility and where price is relative to its recent range — they don't predict direction. A touch of the lower or upper band only hints at a reversal in a ranging market; in a trend, price can "walk" a band for a long time, so check the trend before treating a touch as a bounce or a top.
                </p>
              </div>

              <div className="bg-primary-50 dark:bg-primary-900/20 p-4 rounded-lg border border-primary-200 dark:border-primary-800">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">📊 Advanced Indicators (15 additional)</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">
                  After running an analysis, toggle these category panels for deeper insights:
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <div><strong>Volume:</strong> MVWAP, OBV, A/D Line</div>
                  <div><strong>Momentum:</strong> Stochastic, ADX, CCI, ROC</div>
                  <div><strong>Volatility:</strong> ATR, Keltner Channels, Std Dev</div>
                  <div><strong>Trend:</strong> Parabolic SAR, Ichimoku Cloud, Donchian</div>
                </div>
              </div>

              <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-200 dark:border-indigo-800">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">📄 Financial Summary (Paid Tiers)</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">
                  After running an analysis, click the <strong>Financial Summary</strong> button to view SEC-sourced company financials — updated daily from 10-K and 10-Q filings:
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <div><strong>Income Statement:</strong> Revenue, margins, net income, EBITDA, EPS</div>
                  <div><strong>Balance Sheet:</strong> Cash, debt, equity, current/quick ratios</div>
                  <div><strong>Cash Flow:</strong> Operating CF, CapEx, free cash flow, dividends</div>
                  <div><strong>Ratios:</strong> P/E, PEG, EV/EBITDA, P/S, P/B, ROE, ROA, D/E</div>
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-xs mt-2">
                  Also includes derived DCF inputs (growth rate, estimated WACC, TTM FCF) based on actual financials — use them to pre-populate the DCF Valuation model.
                </p>
              </div>
              
              <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-200 dark:border-orange-800">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>⚠️ Note on Warrants:</strong> Technical analysis on warrants requires special consideration due to their derivative nature, 
                  higher volatility, time decay, and expiration risk. Always compare warrant movements to the underlying common stock and be aware 
                  of the time value component that decreases as expiration approaches.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      {showTrueVwap && ticker && (
        <TrueVwapModal ticker={ticker} onClose={() => setShowTrueVwap(false)} />
      )}
      {showAtrCalc && analysisData && (
        <AtrRiskModal
          atr={analysisData.indicators.atr}
          atrRisk={analysisData.indicators.atr_risk}
          currentPrice={analysisData.current_price}
          analysisDate={analysisData.analysis_date}
          ticker={analysisData.ticker}
          position={atrPosition}
          lots={atrLots}
          lotsTruncated={atrLotsTruncated}
          lotsBeforeAdjust={atrLotsBeforeAdjust}
          onClose={() => setShowAtrCalc(false)}
        />
      )}
      <BackToTop />
    </div>
  );
};

export default TechnicalAnalysis;